/**
 * Admin console API client (T28).
 *
 * A thin wrapper for the read-only `/api/admin/*` endpoints, routed through the
 * shared HttpClient so expired sessions refresh and retry once.
 * The admin console is a lazy-loaded in-app route (features/admin/**), so it is
 * code-split out of the main bundle and only fetched when an admin opens
 * `/admin`. The server `requireAdmin` gate is the real boundary; the calls here
 * are convenience.
 *
 * Auth: the app shell injects its shared HttpClient, so the Bearer and refresh
 * token both come from the shared TokenProvider — localStorage on web and the
 * Android-Keystore mirror on native. A definitive refresh failure clears tokens
 * and dispatches the app-wide login redirect before this wrapper surfaces it.
 *
 * T28.5 Phase 2: the six list fetches take the uniform list query (sort + filter
 * + limit/offset) built from a page's `useAdminTable` params, serialized by
 * `buildAdminListQuery`, and merged with each list's `search` (and any legacy
 * params). The response shape is unchanged (its array key + a real filtered
 * `total`).
 */

import { ApiError, type HttpClient } from '@shopista/shared/api';
import { buildAdminListQuery, type AdminQueryParams } from './useAdminTable';

/**
 * Build the `?…` query string for a list fetch: the uniform list params (when a
 * page passes them) plus any extra params (e.g. `search`, `has_no_brand`). Extra
 * params are appended after, so a blank value is simply omitted by the caller.
 */
function listQuery(
  params: AdminQueryParams | undefined,
  extra: Record<string, string> = {},
): string {
  const sp = params ? buildAdminListQuery(params) : new URLSearchParams();
  for (const [k, v] of Object.entries(extra)) {
    if (v.trim() !== '') sp.set(k, v.trim());
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

let client: HttpClient | null = null;

/** Inject the app shell's shared client, including its refresh-and-retry path. */
export function setAdminHttpClient(httpClient: HttpClient): void {
  client = httpClient;
}

function http(): HttpClient {
  if (!client) throw new Error('Admin HTTP client has not been configured');
  return client;
}

function legacyError(error: unknown, fallback: string): never {
  if (error instanceof ApiError) {
    const message = (error.body as { error?: unknown } | null)?.error;
    const err = new Error(typeof message === 'string' ? message : fallback) as Error & { status?: number };
    err.status = error.status;
    throw err;
  }
  throw error;
}

async function getJson<T>(path: string): Promise<T> {
  try { return await http().getRaw(path) as T; }
  catch (error) { return legacyError(error, `GET ${path} failed: ${(error as ApiError).status}`); }
}

/**
 * Write counterpart to `getJson` for the admin console's write surface (T28.1).
 * Sends a JSON body with POST/PATCH and the same Bearer auth. On a non-2xx the
 * thrown error carries `.status` and, when the server returned a JSON
 * `{ error }`, that message — so the page can show "match name already exists"
 * (409) rather than a bare status code.
 */
async function mutateJson<T>(
  path: string,
  method: 'POST' | 'PATCH' | 'PUT',
  body: unknown,
): Promise<T> {
  try { return (method === 'POST' ? await http().postRaw(path, body) : method === 'PUT' ? await http().putRaw(path, body) : await http().patchRaw(path, body)) as T; }
  catch (error) { return legacyError(error, `${method} ${path} failed: ${(error as ApiError).status}`); }
}

/**
 * DELETE counterpart for the T28.7 guarded deletes. No request body; carries the
 * same Bearer auth. On a 409 (blocked-with-reason) or 404 the thrown error
 * carries `.status` + the server's `{ error }` message, so a page can show the
 * "N stores use this — can't remove" reason verbatim. The 200 body is small
 * (`{ deleted: true }` / the offer's `deleted_at`) and returned to the caller.
 */
async function deleteJson<T>(path: string): Promise<T> {
  try { return await http().deleteRaw(path) as T; }
  catch (error) { return legacyError(error, `DELETE ${path} failed: ${(error as ApiError).status}`); }
}

/** Aggregate counts for the Overview dashboard — mirrors `AdminOverview`. */
export interface AdminOverview {
  total_households: number;
  total_users: number;
  new_signups_week: number;
  new_signups_month: number;
  active_users_week: number;
  active_users_month: number;
  active_sessions: number;
}

export function fetchOverview(): Promise<AdminOverview> {
  return getJson<AdminOverview>('/api/admin/overview');
}

// --- Maintenance action log (T65.1.1 D2 + D6) -------------------------------

/** Open-entry counts by job — the read behind every System-maintenance badge. */
export interface AdminMaintenanceCounts {
  counts: Record<string, number>;
}

export function fetchMaintenanceCounts(): Promise<AdminMaintenanceCounts> {
  return getJson<AdminMaintenanceCounts>('/api/admin/maintenance/counts');
}

// --- Maintenance jobs registry (T65.0) --------------------------------------

/** One `maintenancejobs` row — the stored identity behind a nav item + its queue. */
export interface AdminMaintenanceJob {
  key: string;
  name: string;
  description: string;
  is_enabled: boolean;
  displayorder: number;
  queueroute: string;
  soppath: string;
  settings: Record<string, unknown>;
}

/** Every job, in display order (T65.0 D4 — the jobs list screen; also used to generate the nav). */
export function fetchMaintenanceJobs(): Promise<{ jobs: AdminMaintenanceJob[] }> {
  return getJson<{ jobs: AdminMaintenanceJob[] }>('/api/admin/maintenance/jobs');
}

/** One job's detail (T65.0 D5). */
export function fetchMaintenanceJob(key: string): Promise<{ job: AdminMaintenanceJob }> {
  return getJson<{ job: AdminMaintenanceJob }>(`/api/admin/maintenance/jobs/${encodeURIComponent(key)}`);
}

/** The detail page's Settings section write — `is_enabled` and/or `settings` only. */
export function updateMaintenanceJob(
  key: string,
  patch: { is_enabled?: boolean; settings?: Record<string, unknown> },
): Promise<{ job: AdminMaintenanceJob }> {
  return mutateJson<{ job: AdminMaintenanceJob }>(
    `/api/admin/maintenance/jobs/${encodeURIComponent(key)}`,
    'PATCH',
    patch,
  );
}

export interface AdminDeduplicationEvidence {
  id: string;
  name: string;
  brand_name: string | null;
  active_offer_count: number;
  packings: string[];
  categories: string[];
}

export interface AdminDeduplicationPair {
  first: AdminDeduplicationEvidence;
  second: AdminDeduplicationEvidence;
  kind: 'exact' | 'near';
  similarity: number | null;
  combined_active_offer_count: number;
}

export interface AdminDeduplicationQueueResponse {
  pairs: AdminDeduplicationPair[];
  total: number;
  offset: number;
  limit: number;
  pairs_scanned: number;
  ruled_out_pairs: number;
}

export function fetchDeduplicationQueue(limit = 50, offset = 0): Promise<AdminDeduplicationQueueResponse> {
  return getJson<AdminDeduplicationQueueResponse>(`/api/admin/deduplication?limit=${limit}&offset=${offset}`);
}

export interface AdminDataSufficiencyItem {
  systemproduct_id: string;
  name: string;
  active_offer_count: number;
  normalized_brand_name: string | null;
  image_url: string | null;
  category_ids: string[];
  reasons: Array<'missing-brand' | 'missing-packing' | 'missing-unit'>;
}

export interface AdminDataSufficiencyQueueResponse {
  items: AdminDataSufficiencyItem[];
  total: number;
  limit: number;
  offset: number;
  audit: Array<{ id: string; target_name: string | null; resolution: string; created_at: string }>;
  unmatched_brand_names: string[];
}

export function fetchDataSufficiencyQueue(limit = 50, offset = 0): Promise<AdminDataSufficiencyQueueResponse> {
  return getJson<AdminDataSufficiencyQueueResponse>(`/api/admin/data-sufficiency?limit=${limit}&offset=${offset}`);
}

export function runDataSufficiencyExtraction(): Promise<{ applied: AdminDataSufficiencyQueueResponse['audit'] }> {
  return mutateJson('/api/admin/data-sufficiency/extract', 'POST', {});
}

export function keepDataSufficiency(id: string): Promise<{ id: string }> {
  return mutateJson(`/api/admin/systemproducts/${encodeURIComponent(id)}/data-sufficiency-keep`, 'POST', {});
}

export function ruleDeduplicationPairNotTheSame(firstId: string, secondId: string): Promise<{ first_systemproduct_id: string; second_systemproduct_id: string }> {
  return mutateJson('/api/admin/deduplication/not-the-same', 'POST', {
    first_systemproduct_id: firstId,
    second_systemproduct_id: secondId,
  });
}

/**
 * The owner's answer to an open entry (an escalation or an owner-list item) —
 * generic across every maintenance job (T65.1.1 D6). Never edits the open row;
 * appends the closing ruling entry and resolves with its id.
 */
export function closeMaintenanceAction(
  id: string,
  input: { resolution: string; result?: 'applied' | 'blocked'; result_reason?: string },
): Promise<{ id: string }> {
  return mutateJson<{ id: string }>(
    `/api/admin/maintenance/actions/${encodeURIComponent(id)}/close`,
    'POST',
    input,
  );
}

// --- Users -----------------------------------------------------------------

export interface AdminUserListItem {
  id: string;
  email: string;
  display_name: string;
  household_id: string;
  household_name: string | null;
  registered_at: string;
  last_seen_at: string | null;
  verified: boolean;
  providers: string[];
  active_session_count: number;
  /** Account chip (T35.7.2.5) — `Paid` iff ≥1 active subscription, else `Free`. */
  account: 'Paid' | 'Free';
  /**
   * The Active-plans cell (T35.7.2.5) — recurring `$N/mo` and/or `N one-time`;
   * `pending` when only recorded plans; `—` when none. A finished display string.
   */
  active_plans_summary: string;
}

export interface AdminUserListResponse {
  users: AdminUserListItem[];
  total: number;
}

export interface AdminUserSession {
  id: string;
  device_name: string | null;
  created_at: string;
  last_used_at: string | null;
  expires_at: string;
  revoked_at: string | null;
  state: 'active' | 'expired' | 'revoked';
}

export interface AdminUserMembership {
  household_id: string;
  household_name: string | null;
  role: string;
  status: string;
  joined_at: string;
}

/** One plan in the user-detail Subscriptions panel (T35.7.2.5 D2). */
export interface AdminUserSubscription {
  id: string;
  type: string;
  /** Finished type label (Donation / Back an idea). */
  type_label: string;
  /** The backed idea's title (backIdea only), or null. */
  idea_title: string | null;
  recurring: string;
  /** Finished cadence label (One-time / Weekly / Biweekly / Monthly). */
  billing_label: string;
  amount_cents: number;
  /** Finished per-interval pledge amount, e.g. "$5.00". */
  amount_display: string;
  currency: string;
  status: string;
  /** Finished status label (Active / Pending / Canceled / Failed). */
  status_label: string;
  started_at: string;
  /** Reason of the MOST-RECENT cancellation (canceled rows only), or null. */
  cancellation_reason: string | null;
  cancellation_note: string | null;
}

/** The user-detail Subscriptions panel (T35.7.2.5 D2). */
export interface AdminUserSubscriptionsPanel {
  account: 'Paid' | 'Free';
  active_plan_count: number;
  monthly_equiv_cents: number;
  one_time_active_count: number;
  /** True when the user holds a mid-checkout (`recorded`) plan but is Free. */
  has_pending: boolean;
  subscriptions: AdminUserSubscription[];
}

export interface AdminUserDetail {
  id: string;
  email: string;
  display_name: string;
  household_id: string;
  household_name: string | null;
  registered_at: string;
  last_seen_at: string | null;
  verified: boolean;
  providers: string[];
  sessions: AdminUserSession[];
  memberships: AdminUserMembership[];
  /** The Subscriptions panel (T35.7.2.5 D2) — plan list + summary. */
  subscriptions_panel: AdminUserSubscriptionsPanel;
}

export function fetchUsers(
  search?: string,
  params?: AdminQueryParams,
): Promise<AdminUserListResponse> {
  return getJson<AdminUserListResponse>(
    `/api/admin/users${listQuery(params, { search: search ?? '' })}`,
  );
}

export function fetchUserDetail(id: string): Promise<AdminUserDetail> {
  return getJson<AdminUserDetail>(`/api/admin/users/${encodeURIComponent(id)}`);
}

// --- Households -------------------------------------------------------------

export interface AdminHouseholdListItem {
  id: string;
  name: string;
  registered_at: string;
  member_count: number;
  city: string | null;
  postal_code: string | null;
  selected_store_count: number;
}

export interface AdminHouseholdListResponse {
  households: AdminHouseholdListItem[];
  total: number;
}

export interface AdminHouseholdMember {
  user_id: string;
  email: string;
  display_name: string;
  role: string;
  status: string;
  joined_at: string;
}

export interface AdminHouseholdDetail {
  id: string;
  name: string;
  invite_code: string;
  registered_at: string;
  city: string | null;
  state_province: string | null;
  postal_code: string | null;
  country: string | null;
  selected_store_count: number;
  members: AdminHouseholdMember[];
}

export function fetchHouseholds(
  search?: string,
  params?: AdminQueryParams,
): Promise<AdminHouseholdListResponse> {
  return getJson<AdminHouseholdListResponse>(
    `/api/admin/households${listQuery(params, { search: search ?? '' })}`,
  );
}

export function fetchHouseholdDetail(id: string): Promise<AdminHouseholdDetail> {
  return getJson<AdminHouseholdDetail>(`/api/admin/households/${encodeURIComponent(id)}`);
}

/** A link's source (T28.8 D10) — the DB `provenance` renamed at the boundary. */
export type AdminHouseholdProductLinkSource = 'auto' | 'manual';

/** One system product a household product is linked to (id, name, brand, preference). */
export interface AdminHouseholdProductLink {
  systemproduct_id: string;
  name: string;
  brand_name: string | null;
  preference: string;
  /** Link source — Auto (resolver-matched) / Manual (user override) (T28.8 D10). */
  link_source: AdminHouseholdProductLinkSource;
}

/** Origin — seeded (onboarding catalog) vs hand-added (T28.8 D10). */
export type AdminHouseholdProductOrigin = 'seeded' | 'hand_added';

/** Product-level link-status classification (T28.8 D10, renamed T12.1.8.11). */
export type AdminHouseholdProductLinkStatus =
  | 'auto'
  | 'manual'
  | 'auto_overrides'
  | 'unlinked';

/** Compact usage rollup per household product (T28.8 D10). */
export interface AdminHouseholdProductUsage {
  total_purchases: number;
  last_purchased_at: string | null;
  on_active_list: boolean;
  never_used: boolean;
}

export interface AdminHouseholdProduct {
  id: string;
  name: string;
  category_name: string | null;
  system_products: AdminHouseholdProductLink[];
  /** Origin — seeded vs hand-added (T28.8 D10). */
  origin: AdminHouseholdProductOrigin;
  /** Whether the row was meaningfully edited since creation/seeding (T28.8 D10). */
  edited: boolean;
  /** Product-level link-status classification (T28.8 D10, renamed T12.1.8.11). */
  link_status: AdminHouseholdProductLinkStatus;
  /** Compact usage rollup (T28.8 D10). */
  usage: AdminHouseholdProductUsage;
}

export interface AdminHouseholdProductsResponse {
  products: AdminHouseholdProduct[];
}

/**
 * The household's products + the system product(s) each is linked to (T28.8).
 * Bounded full set (the client table sorts/filters/pages it); spans any
 * household, never the admin's own. 404 on an unknown id. D10 adds per-product
 * Origin / Edited / Link status / Usage axes (each a facet column).
 */
export function fetchHouseholdProducts(id: string): Promise<AdminHouseholdProductsResponse> {
  return getJson<AdminHouseholdProductsResponse>(
    `/api/admin/households/${encodeURIComponent(id)}/products`,
  );
}

// --- Household-product detail drill-down (T28.8 D10) ------------------------

/** One linked system product on the household-product detail (T28.8 D10). */
export interface AdminHouseholdProductLinkRow {
  systemproduct_id: string;
  name: string;
  brand_name: string | null;
  preference: string;
  /** Link source — Auto / Manual (the DB `provenance`, renamed). */
  link_source: AdminHouseholdProductLinkSource;
  linked_at: string;
}

/** A recent purchase row on the household-product detail (T28.8 D10). */
export interface AdminHouseholdProductPurchaseRow {
  id: string;
  purchased_at: string;
  quantity: number | null;
  quantity_unit: string | null;
  price_paid: number | null;
  store_name: string | null;
}

/** The per-product drill-down (T28.8 D10): settings + origin + links + usage. */
export interface AdminHouseholdProductDetail {
  id: string;
  household_id: string;
  name: string;
  category_name: string | null;
  notes: string | null;
  tags: string[];
  default_quantity: number | null;
  allow_substitutions: boolean;
  substitution_notes: string | null;
  always_keep_stocked: boolean;
  expected_price_value: number | null;
  expected_price_currency: string | null;
  cadence_days: number | null;
  origin: AdminHouseholdProductOrigin;
  seeded_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  edited: boolean;
  link_status: AdminHouseholdProductLinkStatus;
  links: AdminHouseholdProductLinkRow[];
  usage: AdminHouseholdProductUsage;
  cadence: {
    total_purchases: number;
    first_purchased_at: string | null;
    last_purchased_at: string | null;
    avg_days_between_purchases: number | null;
    predicted_next_purchase_at: string | null;
  } | null;
  recent_purchases: AdminHouseholdProductPurchaseRow[];
}

/**
 * The per-product drill-down (T28.8 D10) — a household product's full settings +
 * origin, every linked system product (Link source / preference / linked-at),
 * and its usage. Spans any household; 404 on an unknown product (or one not under
 * the given household).
 */
export function fetchHouseholdProductDetail(
  householdId: string,
  productId: string,
): Promise<AdminHouseholdProductDetail> {
  return getJson<AdminHouseholdProductDetail>(
    `/api/admin/households/${encodeURIComponent(householdId)}/products/${encodeURIComponent(productId)}`,
  );
}

// --- System health ----------------------------------------------------------

export interface AdminSyncRunSummary {
  adapter_name: string;
  status: string;
  started_at: string;
  finished_at: string | null;
  rows_processed: number;
  rows_inserted: number;
  rows_updated: number;
  rows_delisted: number;
  error: string | null;
}

export interface AdminCatalogStats {
  system_products: number;
  retailer_offers: number;
  active_flyer_deals: number;
  brands: number;
  retailers: number;
  store_locations: number;
}

export interface AdminSystemHealth {
  sync_runs: AdminSyncRunSummary[];
  catalog: AdminCatalogStats;
}

export function fetchSystemHealth(): Promise<AdminSystemHealth> {
  return getJson<AdminSystemHealth>('/api/admin/system-health');
}

// --- SystemProduct inspector ------------------------------------------------

/** The shared classifier's junk label (T28.12 + T65.1.1 Fix 3) — null = a real product. */
export type SystemProductJunkClass = 'pack-only' | 'numeric-fragment' | 'bundle';

export interface AdminSystemProductSearchItem {
  id: string;
  name: string;
  brand_id: string | null;
  brand_name: string | null;
  normalized_brand_name: string | null;
  active_offer_count: number;
  category_ids: string[];
  /** ISO creation timestamp (T65.1.1 G1) — the new-arrivals read sorts on this. */
  created_at: string;
  /** The classifier's label for this row's name (T28.12); null = real product. */
  junk_class: SystemProductJunkClass | null;
  /**
   * The three reference signals behind cleanup-safety (T28.12) — populated when
   * a junk filter, `created_since`, or `from_split` is active (the review
   * surfaces); null on the plain catalog list.
   * 0 across all three → safe to delete.
   */
  reference_counts: {
    active_offers: number;
    household_product_pins: number;
    household_pins: number;
  } | null;
}

export interface AdminSystemProductSearchResponse {
  items: AdminSystemProductSearchItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface AdminSystemProductSearchParams {
  query?: string;
  brand_id?: string;
  has_no_brand?: boolean;
  category_id?: string;
  orphaned?: boolean;
  /**
   * Junk-class filter (T28.12) — `'pack-only'` / `'numeric-fragment'` /
   * `'bundle'` (T65.1.1 Fix 3) / `'any'`. Sent as `filter.junk`; switches the
   * list into cleanup mode (each row carries `junk_class` + `reference_counts`).
   */
  junk?: SystemProductJunkClass | 'any';
  /**
   * New-arrivals filter (T65.1.1 G1) — only rows created at/after this ISO
   * timestamp. Pair with `list.sort = { key: 'created_at', dir: 'desc' }`.
   */
  created_since?: string;
  /**
   * Split-backlog filter (T65.1.1 G1) — only rows whose source flyer deal links
   * more than one SystemProduct. Composable with `created_since`; usable alone.
   */
  from_split?: boolean;
  /** Uniform list params (T28.5): sort + `filter.brand`/`filter.status` + paging. */
  list?: AdminQueryParams;
}

export interface AdminSystemProductOffer {
  /** Entity kind — a store/retail offer. The flyer-deal sibling is `'flyer_deal'` (T28.8). */
  kind: 'retail_offer';
  id: string;
  storelocation_id: string;
  store_name: string;
  /** The chain (retailer) the offer's store belongs to — the offer's "source" (T28.8). */
  retailer_id: string | null;
  retailer_name: string | null;
  /**
   * The offer's store geodata (T28.8). The store row has no province column, so
   * `store_province` is the 2-letter province derived server-side from the
   * postal-code FSA; `null` when the store has no postal code. `pricing_zone_*`
   * is the store's linked `retailerpricingzones` row.
   */
  store_postal_code: string | null;
  store_province: string | null;
  pricing_zone_id: string | null;
  pricing_zone_name: string | null;
  title: string;
  regular_price: number | null;
  price_currency: string | null;
  package_size_value: number | null;
  package_size_unit: string | null;
  pack_count: number | null;
  availability_status: string | null;
  last_seen_at: string | null;
  updated_at: string;
}

/**
 * A flyer deal linked to this system product via `flyerdeals.systemproduct_id`
 * (T28.8 enhancement — the T12.1.8.6 link). Sibling of `AdminSystemProductOffer`;
 * the UI merges both arrays into one table with an explicit Type column.
 *
 * Three distinct axes, kept apart: `retailer_name` is the CHAIN that published
 * the deal (Loblaws / No Frills — same axis as a store offer's chain); `source`
 * is the FLYER SOURCE the adapter wrote (`flipp`); `kind` is the entity kind
 * (always `'flyer_deal'` here). Do not conflate them.
 */
export interface AdminSystemProductFlyerDeal {
  kind: 'flyer_deal';
  id: string;
  storelocation_id: string | null;
  store_name: string | null;
  retailer_id: string | null;
  retailer_name: string | null;
  /** The flyer source (`flipp`, …) — NOT the chain. */
  source: string;
  store_postal_code: string | null;
  store_province: string | null;
  pricing_zone_id: string | null;
  pricing_zone_name: string | null;
  title: string;
  /** The promotional / sale price. */
  sale_price: number | null;
  /** The pre-promo regular price the adapter captured, when present. */
  regular_price: number | null;
  price_currency: string | null;
  valid_from: string | null;
  valid_to: string | null;
  updated_at: string;
}

export interface AdminSystemProductCategoryTag {
  id: string;
  name: string;
}

/** T55.4 — the Attribute-junction sibling of `AdminSystemProductCategoryTag`
 * (mirrors the backend's `AdminSystemProductAttributeTag`). */
export interface AdminSystemProductAttributeTag {
  id: string;
  name: string;
}

export interface AdminSystemProductPinningCounts {
  preferred: number;
  substitute: number;
  exclude: number;
}

/** The cleanup-gate inputs behind `cleanup_eligible` (T28.8 D9). */
export interface AdminSystemProductCleanupBreakdown {
  active_offer_count: number;
  product_pin_count: number;
  household_pin_count: number;
  days_since_updated: number;
  staleness_window_days: number;
  stale: boolean;
}

/** The ingestion run recovered through a linked offer's history (T65.1.1 G2). */
export interface AdminSystemProductIngestionRun {
  id: string;
  adapter_name: string;
  started_at: string;
  finished_at: string | null;
}

/** A split-created row's original combo + siblings (T65.1.1 G2). */
export interface AdminSystemProductSplitProvenance {
  combo_title: string;
  sibling_lines: { id: string; name: string }[];
}

/** "Where did this row come from?" (T65.1.1 G2). */
export interface AdminSystemProductSourceEvidence {
  offer_titles: string[];
  flyer_deal_titles: string[];
  ingestion_run: AdminSystemProductIngestionRun | null;
  /** `false` → no raw source title survives; the detail says so explicitly. */
  has_evidence: boolean;
  split: AdminSystemProductSplitProvenance | null;
}

export interface AdminSystemProductDetail {
  id: string;
  name: string;
  brand_id: string | null;
  brand_name: string | null;
  normalized_brand_name: string | null;
  /** The matching key (`normalizedname`) — shown beside the signature (T28.8 D9). */
  normalized_name: string;
  signature: string;
  /** Product image (T28.8 D9). */
  image_url: string | null;
  /** How the row was created — `seed` / `auto` (resolver) / `manual` (T28.8 D9). */
  origin: string;
  /** Province availability from `systemproducts_regions` (T28.8 D9). */
  region_provinces: string[];
  created_at: string;
  updated_at: string;
  /** Live (non-tombstoned) Category tags — a durable per-tag removal drops
   * out of this list (server-filtered). */
  category_tags: AdminSystemProductCategoryTag[];
  /** Live (non-tombstoned) Attribute tags — same filter as `category_tags`. */
  attribute_tags: AdminSystemProductAttributeTag[];
  offers: AdminSystemProductOffer[];
  /**
   * The flyer deals linked to this system product (T28.8 enhancement) — `[]` when
   * none are linked yet (the common case). The UI shows these and `offers` in one
   * table distinguished by a Type column.
   */
  flyer_deals: AdminSystemProductFlyerDeal[];
  pinning_counts: AdminSystemProductPinningCounts;
  /** Household-level preference count — the second reference side (T28.7). */
  household_pin_count: number;
  cleanup_eligible: boolean;
  /** The breakdown behind `cleanup_eligible` (T28.8 D9). */
  cleanup_breakdown: AdminSystemProductCleanupBreakdown;
  /** "Where did this row come from?" (T65.1.1 G2). */
  source_evidence: AdminSystemProductSourceEvidence;
}

export function fetchSystemProducts(
  params: AdminSystemProductSearchParams = {},
): Promise<AdminSystemProductSearchResponse> {
  // Start from the uniform list query (sort + `filter.*` + limit/offset) when a
  // page passes it, then layer the catalog-specific params (free-text `query`,
  // the legacy `has_no_brand`/`orphaned`/`brand_id`/`category_id` flags).
  const sp = params.list ? buildAdminListQuery(params.list) : new URLSearchParams();
  if (params.query && params.query.trim()) sp.set('query', params.query.trim());
  if (params.brand_id) sp.set('brand_id', params.brand_id);
  if (params.has_no_brand) sp.set('has_no_brand', 'true');
  if (params.category_id) sp.set('category_id', params.category_id);
  if (params.orphaned) sp.set('orphaned', 'true');
  if (params.junk) sp.set('filter.junk', params.junk);
  if (params.created_since) sp.set('created_since', params.created_since);
  if (params.from_split) sp.set('from_split', 'true');
  const qs = sp.toString();
  return getJson<AdminSystemProductSearchResponse>(
    `/api/admin/systemproducts${qs ? `?${qs}` : ''}`,
  );
}

/** One id's outcome in a bulk delete (T28.12). */
export interface BulkDeleteItemResult {
  id: string;
  deleted: boolean;
  /** Present only when `deleted` is false — the named reference / not-found reason. */
  blockedReason?: string;
}

export interface BulkDeleteResult {
  results: BulkDeleteItemResult[];
}

/**
 * Bulk reference-safe delete of junk system products (T28.12). The server loops
 * the per-row reference gate, so a referenced row comes back `deleted:false` with
 * a named reason and is left intact. 400 on an empty / over-cap (>500) batch.
 * Resolves with per-id results regardless of how many were skipped.
 */
export function bulkDeleteSystemProducts(ids: string[]): Promise<BulkDeleteResult> {
  return mutateJson<BulkDeleteResult>('/api/admin/systemproducts/bulk-delete', 'POST', { ids });
}

/** The junk-health trigger (T28.12 D5, + T65.1.1 Fix 3's `bundle` class): count
 * per class + latest cleanup-run counters. */
export interface AdminJunkHealth {
  counts: { any: number; 'pack-only': number; 'numeric-fragment': number; bundle: number };
  pulse: { rows_purged: number; rows_deactivated: number; last_run_at: string | null } | null;
}

/** The in-app junk-visibility trigger (T28.12 D5 / AC6) — count per class + cleanup pulse. */
export function fetchJunkHealth(): Promise<AdminJunkHealth> {
  return getJson<AdminJunkHealth>('/api/admin/systemproducts/junk-health');
}

/** Per-source creation-time reject readout (T65.1.6 D4, G11). */
export interface AdminIngestionRejectSource {
  source: string;
  count: number;
  /** Raw titles, newest first, capped at 5 per source. */
  examples: string[];
}

export interface AdminIngestionRejectReadout {
  since: string;
  sources: AdminIngestionRejectSource[];
}

/**
 * The Ingestion review rule strip's reject-count feed. `since` defaults
 * server-side to the last ingestion-review pass's start (omit it to get that
 * anchor for free); `source` narrows to one Integration data source.
 */
export function fetchIngestionRejectCounts(params?: {
  since?: string;
  source?: string;
}): Promise<AdminIngestionRejectReadout> {
  const sp = new URLSearchParams();
  if (params?.since) sp.set('since', params.since);
  if (params?.source) sp.set('source', params.source);
  const qs = sp.toString();
  return getJson<AdminIngestionRejectReadout>(`/api/admin/ingestion-rejects${qs ? `?${qs}` : ''}`);
}

export function fetchSystemProductDetail(id: string): Promise<AdminSystemProductDetail> {
  return getJson<AdminSystemProductDetail>(
    `/api/admin/systemproducts/${encodeURIComponent(id)}`,
  );
}

/**
 * Guarded Remove of a system product on the corrected both-sides gate (T28.7):
 * resolves on 200 (no active offers, no household-product or household
 * preferences, stale); rejects with the server's blocked reason on 409 (a
 * reference on either side, or not yet stale), or "SystemProduct not found" on
 * 404.
 */
export function removeSystemProduct(id: string): Promise<DeleteResult> {
  return deleteJson<DeleteResult>(`/api/admin/systemproducts/${encodeURIComponent(id)}`);
}

/**
 * Create a manual system product (T28.8 D7). `category_ids` are seed-category ids.
 * The signature is derived server-side from the name + chosen brand; a signature
 * collision rejects with a 409 whose `.message` is the server's hint (the existing
 * row is the Merge survivor). Resolves with the created product's detail.
 */
export interface SystemProductWriteInput {
  name: string;
  brand_id?: string | null;
  image_url?: string | null;
  category_ids?: string[];
  maintenance_job?: 'data-sufficiency';
}

export function createSystemProduct(
  input: SystemProductWriteInput,
): Promise<AdminSystemProductDetail> {
  return mutateJson<AdminSystemProductDetail>('/api/admin/systemproducts', 'POST', input);
}

/** T36.2 / ADR-015 — an admin packing correction (T65.1.1 D6 wires this into the
 * Ingestion review "Correct" action). `pack_count`/`value` optional; `unit`
 * required. Routes through the same writer as offer sync (`source: 'admin'`). */
export interface SystemProductPackingInput {
  pack_count?: number | null;
  value?: number | string | null;
  unit: string;
}

export function applySystemProductPacking(
  id: string,
  input: SystemProductPackingInput,
): Promise<{ id: string }> {
  return mutateJson<{ id: string }>(
    `/api/admin/systemproducts/${encodeURIComponent(id)}/packings`,
    'POST',
    input,
  );
}

/** Edit a system product (T28.8 D7). 409 on a signature collision with another row. */
export function updateSystemProduct(
  id: string,
  input: SystemProductWriteInput,
): Promise<AdminSystemProductDetail> {
  return mutateJson<AdminSystemProductDetail>(
    `/api/admin/systemproducts/${encodeURIComponent(id)}`,
    'PATCH',
    input,
  );
}

/**
 * Merge a duplicate/split system product (`loserId`) into a survivor (`survivorId`)
 * (T28.8 D8). Every reference re-points onto the survivor and the loser is deleted.
 * 400 self-merge / missing loser; 404 unknown.
 */
export interface SystemProductMergeResult {
  survivor_id: string;
  repointed: Record<string, number>;
}

export function mergeSystemProducts(
  survivorId: string,
  loserId: string,
): Promise<SystemProductMergeResult> {
  return mutateJson<SystemProductMergeResult>(
    `/api/admin/systemproducts/${encodeURIComponent(survivorId)}/merge`,
    'POST',
    { loser_id: loserId },
  );
}

/** `reviewedDeleteSystemProduct`'s outcome — mirrors `GuardedDeleteResult`, plus
 * the escalation opened when the row is still referenced (T65.1.1 G3). */
export interface ReviewedDeleteResult {
  deleted: boolean;
  blockers: { kind: string; count: number; phrase?: string }[];
  escalation_id?: string;
}

/**
 * Reviewed-delete (T65.1.1 G3 / D5) — a row the reviewer judged NOT a real
 * product, no staleness requirement (unlike `removeSystemProduct`). Unreferenced
 * → deleted, one action-log entry. Referenced → nothing deleted; an escalation
 * opens instead (`escalation_id`), answered via `closeMaintenanceAction`. 404 on
 * an unknown id.
 */
export function reviewedDeleteSystemProduct(
  id: string,
  reason: string,
): Promise<ReviewedDeleteResult> {
  return mutateJson<ReviewedDeleteResult>(
    `/api/admin/systemproducts/${encodeURIComponent(id)}/reviewed-delete`,
    'POST',
    { reason },
  );
}

/** The ingestion-review queue's "no defect found" ruling (T65.1.1 D6) — logs
 * one action-log entry; mutates nothing on the row. 404 on an unknown id. */
export function keepSystemProduct(id: string, reason?: string): Promise<{ id: string }> {
  return mutateJson<{ id: string }>(
    `/api/admin/systemproducts/${encodeURIComponent(id)}/keep`,
    'POST',
    { reason },
  );
}

// --- Brand → linked system products (T28.8 D5) ------------------------------

export interface AdminBrandSystemProduct {
  id: string;
  name: string;
  signature: string;
  active_offer_count: number;
}

export interface AdminBrandSystemProductsResponse {
  system_products: AdminBrandSystemProduct[];
}

/** The system products whose `brand_id` is this brand (T28.8 D5). 404 unknown brand. */
export function fetchBrandSystemProducts(
  id: string,
): Promise<AdminBrandSystemProductsResponse> {
  return getJson<AdminBrandSystemProductsResponse>(
    `/api/admin/brands/${encodeURIComponent(id)}/system-products`,
  );
}

// --- Seed categories (the system-product create/edit picker, T28.8 D7) ------

export interface AdminCategory {
  id: string;
  name: string;
  parent_category_id: string | null;
}

export interface AdminCategoriesResponse {
  categories: AdminCategory[];
}

export function fetchAdminCategories(): Promise<AdminCategoriesResponse> {
  return getJson<AdminCategoriesResponse>('/api/admin/categories');
}

// --- Retailers (T28.1, write) ----------------------------------------------

export interface AdminRetailer {
  id: string;
  name: string;
  normalizedname: string;
  website_url: string | null;
  logo_url: string | null;
  country: string | null;
  /** Lifecycle status (`'active' | 'inactive'`) — T28.7 deactivate/reactivate. */
  status: string;
  store_count: number;
  created_at: string;
  updated_at: string;
}

export interface AdminRetailerListResponse {
  retailers: AdminRetailer[];
  total: number;
}

export interface CreateRetailerInput {
  name: string;
  /** Optional — the backend derives the match name from `name` (DB trigger). */
  normalizedname?: string;
  website_url?: string | null;
  logo_url?: string | null;
  country?: string | null;
}

export type UpdateRetailerInput = Partial<CreateRetailerInput>;

export function fetchRetailers(
  search?: string,
  params?: AdminQueryParams,
): Promise<AdminRetailerListResponse> {
  return getJson<AdminRetailerListResponse>(
    `/api/admin/retailers${listQuery(params, { search: search ?? '' })}`,
  );
}

export function createRetailer(input: CreateRetailerInput): Promise<AdminRetailer> {
  return mutateJson<AdminRetailer>('/api/admin/retailers', 'POST', input);
}

export function updateRetailer(id: string, input: UpdateRetailerInput): Promise<AdminRetailer> {
  return mutateJson<AdminRetailer>(
    `/api/admin/retailers/${encodeURIComponent(id)}`,
    'PATCH',
    input,
  );
}

/** Shape returned by a successful guarded hard-delete (200). */
export interface DeleteResult {
  deleted: true;
}

/**
 * Guarded hard-delete a chain (T28.7). Resolves on 200 (unreferenced → deleted);
 * rejects with an Error whose `.message` is the server's blocked reason on 409
 * ("N stores use this — can't remove"), or "Retailer not found" on 404.
 */
export function deleteRetailer(id: string): Promise<DeleteResult> {
  return deleteJson<DeleteResult>(`/api/admin/retailers/${encodeURIComponent(id)}`);
}

/**
 * Deactivate a chain (T28.7) — reversible soft-off. Its deals/offers stop
 * surfacing to shoppers and it drops from the store-picker; the row is kept.
 * Returns the updated retailer (status `'inactive'`).
 */
export function deactivateRetailer(id: string): Promise<AdminRetailer> {
  return mutateJson<AdminRetailer>(
    `/api/admin/retailers/${encodeURIComponent(id)}/deactivate`,
    'POST',
    {},
  );
}

/** Reactivate a chain (T28.7) — restores its deals/offers + picker visibility. */
export function reactivateRetailer(id: string): Promise<AdminRetailer> {
  return mutateJson<AdminRetailer>(
    `/api/admin/retailers/${encodeURIComponent(id)}/reactivate`,
    'POST',
    {},
  );
}

export interface AdminUnrecognizedChain {
  brand: string;
  deal_count: number;
}

export interface AdminUnrecognizedChainsResponse {
  chains: AdminUnrecognizedChain[];
  total: number;
}

export function fetchUnrecognizedChains(): Promise<AdminUnrecognizedChainsResponse> {
  return getJson<AdminUnrecognizedChainsResponse>('/api/admin/retailers/unrecognized-chains');
}

// --- Store locations (T28.1, write) ----------------------------------------

export interface AdminStoreLocation {
  id: string;
  name: string;
  normalizedname: string;
  address: string | null;
  postal_code: string | null;
  retailer_id: string | null;
  retailer_name: string | null;
  retailerpricingzone_id: string | null;
  is_online: boolean;
  /** Lifecycle status (`'active' | 'inactive'`) — T28.7 deactivate/reactivate. */
  status: string;
  created_at: string;
  /**
   * BUG52 — the store's coordinates, or `null` when unset. Number, not the raw
   * Postgres-numeric string — matches `CreateStoreLocationInput`/
   * `UpdateStoreLocationIdentityInput`'s `latitude?: number` below, so a value
   * read here can be resubmitted unchanged without a string/number conversion.
   */
  latitude: number | null;
  longitude: number | null;
}

export interface AdminStoreLocationListResponse {
  store_locations: AdminStoreLocation[];
  total: number;
}

export function fetchStoreLocations(
  search?: string,
  params?: AdminQueryParams,
): Promise<AdminStoreLocationListResponse> {
  return getJson<AdminStoreLocationListResponse>(
    `/api/admin/store-locations${listQuery(params, { search: search ?? '' })}`,
  );
}

export function fetchUnlinkedStoreLocations(
  params?: AdminQueryParams,
): Promise<AdminStoreLocationListResponse> {
  return getJson<AdminStoreLocationListResponse>(
    `/api/admin/store-locations/unlinked${listQuery(params)}`,
  );
}

/** Set (`retailerId` string) or clear (`null`) a store's retailer link. */
export function setStoreLocationRetailer(
  id: string,
  retailerId: string | null,
): Promise<AdminStoreLocation> {
  return mutateJson<AdminStoreLocation>(
    `/api/admin/store-locations/${encodeURIComponent(id)}`,
    'PATCH',
    { retailer_id: retailerId },
  );
}

/**
 * Guarded store ADD (T28.7 Phase 3) — for a genuine store Google doesn't list
 * yet. `name` + `postal_code` + `retailer_id` (chain) are required; `address` is
 * optional. Coordinates are the operator's pin (`latitude`/`longitude`) OR
 * geocoded from the address server-side — they're required for selectability, so
 * the server 422s when neither a pin nor a geocodable address is given. Creates
 * the row with `place_id` null. Resolves on 201 (the created store); rejects with
 * the server's message on 400 (missing field) / 404 (unknown chain) / 422 (no
 * coords).
 */
export interface CreateStoreLocationInput {
  name: string;
  postal_code: string;
  retailer_id: string;
  address?: string | null;
  latitude?: number | undefined;
  longitude?: number | undefined;
  country?: string | null;
}

export function createStoreLocation(input: CreateStoreLocationInput): Promise<AdminStoreLocation> {
  return mutateJson<AdminStoreLocation>('/api/admin/store-locations', 'POST', input);
}

/**
 * Correct a store's identity in place (T28.7 Phase 3) — for bad Google data. Any
 * of name / address / postal_code / coordinates. A postal-code change re-resolves
 * the pricing zone from the store's current chain. Resolves on 200 (the updated
 * store); rejects with "Store location not found" on 404. The chain link is the
 * separate `setStoreLocationRetailer`.
 */
export interface UpdateStoreLocationIdentityInput {
  name?: string | undefined;
  address?: string | null | undefined;
  postal_code?: string | null | undefined;
  latitude?: number | undefined;
  longitude?: number | undefined;
}

export function updateStoreLocationIdentity(
  id: string,
  input: UpdateStoreLocationIdentityInput,
): Promise<AdminStoreLocation> {
  return mutateJson<AdminStoreLocation>(
    `/api/admin/store-locations/${encodeURIComponent(id)}/identity`,
    'PATCH',
    input,
  );
}

/**
 * Guarded hard-delete a store (T28.7). Resolves on 200; rejects with the
 * server's blocked reason on 409 (none of the 9 FK tables / no household selected
 * it), or "Store location not found" on 404.
 */
export function deleteStoreLocation(id: string): Promise<DeleteResult> {
  return deleteJson<DeleteResult>(`/api/admin/store-locations/${encodeURIComponent(id)}`);
}

/**
 * Deactivate a store (T28.7) — reversible soft-off. Its deals/offers stop
 * surfacing to shoppers and it drops from the store-picker; a household that
 * already selected it keeps the selection (it reads as *closed*). Returns the
 * updated store (status `'inactive'`).
 */
export function deactivateStoreLocation(id: string): Promise<AdminStoreLocation> {
  return mutateJson<AdminStoreLocation>(
    `/api/admin/store-locations/${encodeURIComponent(id)}/deactivate`,
    'POST',
    {},
  );
}

/** Reactivate a store (T28.7) — restores its deals/offers + picker visibility. */
export function reactivateStoreLocation(id: string): Promise<AdminStoreLocation> {
  return mutateJson<AdminStoreLocation>(
    `/api/admin/store-locations/${encodeURIComponent(id)}/reactivate`,
    'POST',
    {},
  );
}

// --- Discover-by-area + bulk-save (T26.4) ----------------------------------
//
// The admin discover tool: pick a center (address or coords) + radius, run
// Google discovery (admin-only, on-demand), review candidates, bulk-save the
// chosen set to the shared catalogue (auto-linked to their chain).

/** One discovered store candidate from the discover-by-area route. */
export interface DiscoveredStoreCandidate {
  placeId: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  distanceKm: number;
  suggestedSelected: boolean;
  postalCode?: string;
  website?: string;
}

export interface DiscoverStoresResponse {
  candidates: DiscoveredStoreCandidate[];
}

/**
 * The operator-chosen discovery categories (T26.4.1) — drive the Place-type
 * sweep. `grocery` is the unfiltered base; `pharmacy` + `warehouse` are
 * roster-filtered widen categories. Omitting `categories` sweeps all three.
 */
export type DiscoveryCategory = 'grocery' | 'pharmacy' | 'warehouse';

/**
 * Discover-by-area (T26.4). `center` is a free-form address (geocoded
 * server-side) or explicit `{ latitude, longitude }`. `categories` (T26.4.1) is
 * the operator's chosen Place-type sweep; omitted = all three. Returns the
 * candidate list — NO persistence. Rejects with the server's message on 400 (bad
 * body) / 422 (an address that can't be geocoded).
 */
export function discoverStoreLocations(input: {
  center: { address: string; country?: string } | { latitude: number; longitude: number };
  radiusKm?: number;
  categories?: DiscoveryCategory[];
}): Promise<DiscoverStoresResponse> {
  return mutateJson<DiscoverStoresResponse>(
    '/api/admin/store-locations/discover',
    'POST',
    input,
  );
}

/** Per-candidate outcome of a bulk save. */
export interface BulkSaveStoreResult {
  placeId: string;
  status: 'saved' | 'skipped';
  storeLocationId: string | null;
  retailer_id: string | null;
  reason?: string;
}

export interface BulkSaveStoresResponse {
  results: BulkSaveStoreResult[];
  saved: number;
  skipped: number;
}

/**
 * Bulk-save reviewed candidates to the catalogue (T26.4). Each is upserted as a
 * shared `storelocations` row keyed on `place_id` (re-saving updates, never
 * duplicates), with `retailer_id` + `retailerpricingzone_id` auto-resolved
 * (bias-to-null → the unlinked view). Returns per-candidate results.
 */
export function bulkSaveDiscoveredStores(
  candidates: DiscoveredStoreCandidate[],
): Promise<BulkSaveStoresResponse> {
  return mutateJson<BulkSaveStoresResponse>(
    '/api/admin/store-locations/bulk-save',
    'POST',
    { candidates },
  );
}

// --- Store-detail "currently published" reads (T28.4) ----------------------
//
// Both reuse the shopper-facing per-store resolution. The flyer-deal shape is a
// subset of the backend `ResolvedFlyerDeal`; the offer shape a subset of
// `RetailerOfferWithDetails` — only the fields the store-detail tables render.

/** The matched RetailerOffer a flyer deal resolved to (T28.8 D6), or null. */
export interface AdminMatchedRetailerOffer {
  id: string;
  store_name: string;
  retailer_name: string | null;
  title: string;
  regular_price: number | null;
  price_currency: string | null;
}

export interface AdminStoreFlyerDeal {
  id: string;
  title: string;
  sale_price: number;
  sale_description?: string;
  /** T64.1 D8 — what `sale_price` is charged for (per item / per pound / per
   *  kilogram); absent when the source supplied none. */
  price_basis?: string;
  valid_from: string;
  valid_to: string;
  is_expired: boolean;
  /**
   * The matched RetailerOffer (store-specific regular price) this deal resolved
   * to via `matched_retaileroffer_id` (T28.8 D6) — the RetailerOffer side of the
   * flyer-deal links. null when the deal matched nothing (or the matched offer
   * was since delisted). The system-product side stays absent until the
   * FlyerDeal→SystemProduct link is built (a separate data-model task).
   */
  matched_offer?: AdminMatchedRetailerOffer | null;
}

export interface AdminStoreDealsResponse {
  deals: AdminStoreFlyerDeal[];
}

export interface AdminRetailerOffer {
  id: string;
  title: string;
  brand?: string;
  regular_price?: number;
  price_currency: string;
  package_size_value?: number;
  package_size_unit?: string;
  pack_count?: number;
}

export interface AdminRetailerOffersResponse {
  offers: AdminRetailerOffer[];
  total: number;
}

/** Flyer deals published at this store (override + FSA + retailer gating). */
export function fetchStoreLocationDeals(id: string): Promise<AdminStoreDealsResponse> {
  return getJson<AdminStoreDealsResponse>(
    `/api/admin/store-locations/${encodeURIComponent(id)}/deals`,
  );
}

/** Retailer offers at this store (consumer `listOffersByStore`, capped). */
export function fetchStoreLocationOffers(id: string): Promise<AdminRetailerOffersResponse> {
  return getJson<AdminRetailerOffersResponse>(
    `/api/admin/store-locations/${encodeURIComponent(id)}/offers`,
  );
}

/** Shape returned by a successful offer soft-delete (200). */
export interface OfferSoftDeleteResult {
  deleted: true;
  deleted_at: string | null;
}

/**
 * Soft-delete a stuck/bad retailer offer (T28.7) — stamps `deleted_at` so it
 * drops out of active reads. Resolves on 200 (re-appears if the sync re-ingests
 * it); rejects with "Offer not found" on 404. Callable from the store-detail /
 * system-product-detail offer rows.
 */
export function deleteOffer(id: string): Promise<OfferSoftDeleteResult> {
  return deleteJson<OfferSoftDeleteResult>(`/api/admin/offers/${encodeURIComponent(id)}`);
}

// --- Brands (T28.2, write) --------------------------------------------------

export interface AdminBrand {
  id: string;
  name: string;
  normalized_name: string;
  country: string | null;
  logo_url: string | null;
  website_url: string | null;
  is_private_label: boolean;
  parent_chain_name: string | null;
  verified: boolean;
  status: string;
  offer_count: number;
  product_count: number;
  created_at: string;
  updated_at: string;
}

export interface AdminBrandListResponse {
  brands: AdminBrand[];
  total: number;
}

export interface CreateBrandInput {
  name: string;
  country?: string | null;
  logo_url?: string | null;
  website_url?: string | null;
  is_private_label?: boolean;
  parent_chain_name?: string | null;
  verified?: boolean;
}

export type UpdateBrandInput = Partial<CreateBrandInput> & { status?: 'active' | 'retired' };

export function fetchBrands(
  search?: string,
  params?: AdminQueryParams,
): Promise<AdminBrandListResponse> {
  return getJson<AdminBrandListResponse>(
    `/api/admin/brands${listQuery(params, { search: search ?? '' })}`,
  );
}

/**
 * Fetch the FULL brand list (no paging) for the merge-survivor picker. The list
 * view is server-paged (T28.5), so the loaded page can't be the merge target
 * source — a brand merge must be able to target ANY brand, not just one on the
 * current page. Passing no `params` omits limit/offset, so the brands endpoint
 * returns its whole filtered set (the brands list default is the full set).
 */
export function fetchAllBrands(): Promise<AdminBrand[]> {
  return fetchBrands().then((r) => r.brands);
}

export function createBrand(input: CreateBrandInput): Promise<AdminBrand> {
  return mutateJson<AdminBrand>('/api/admin/brands', 'POST', input);
}

export function updateBrand(id: string, input: UpdateBrandInput): Promise<AdminBrand> {
  return mutateJson<AdminBrand>(`/api/admin/brands/${encodeURIComponent(id)}`, 'PATCH', input);
}

export function retireBrand(id: string): Promise<AdminBrand> {
  return mutateJson<AdminBrand>(`/api/admin/brands/${encodeURIComponent(id)}/retire`, 'POST', {});
}

/**
 * Guarded hard-delete a brand (T28.7). Resolves on 200 (unreferenced → deleted);
 * rejects with the server's blocked reason on 409 (offers / products /
 * preferences point at it — the operator Retires it instead), or "Brand not
 * found" on 404.
 */
export function deleteBrand(id: string): Promise<DeleteResult> {
  return deleteJson<DeleteResult>(`/api/admin/brands/${encodeURIComponent(id)}`);
}

/** Merge a duplicate (`loserId`) into a survivor (`survivorId`). */
export function mergeBrands(survivorId: string, loserId: string): Promise<{
  survivor: AdminBrand;
  repointed: Record<string, number>;
}> {
  return mutateJson(
    `/api/admin/brands/${encodeURIComponent(survivorId)}/merge`,
    'POST',
    { loser_id: loserId },
  );
}

export interface AdminUnmatchedBrandName {
  normalized_brand_name: string;
  product_count: number;
}

export interface AdminUnmatchedBrandNamesResponse {
  names: AdminUnmatchedBrandName[];
  total: number;
}

export function fetchUnmatchedBrandNames(): Promise<AdminUnmatchedBrandNamesResponse> {
  return getJson<AdminUnmatchedBrandNamesResponse>('/api/admin/brands/unmatched-names');
}

// --- Coverage failures (T12.1.5.1.10 Phase 5) -------------------------------

/** One selected store affected by a failing FSA (bounded inline set). */
export interface AdminCoverageFailureStore {
  id: string;
  name: string;
}

/**
 * One persistently-failing FSA (its automatic coverage re-attempts are
 * exhausted). The backend owns the "exhausted" predicate — the list shows
 * whatever `GET /coverage-failures` returns; the UI never re-derives it.
 */
export interface AdminCoverageFailure {
  /** The forward-sortation area, e.g. `C1A`. One row per failing FSA. */
  fsa: string;
  /** The postal code the latest fetch probed, e.g. `C1A 1A1`. */
  postal_code: string;
  /** The bucketed outcome of the latest fetch (never `items-ingested` here). */
  outcome: 'list-empty' | 'list-nonempty-but-zero-parsed' | 'errored';
  /** The latest fetch's error text (only meaningful for the `errored` bucket). */
  error: string | null;
  /** The latest fetch's attempt number (the cap has been reached). */
  attempt_count: number;
  /** The latest fetch's time (`postalfetches.fetched_at`, ISO). */
  last_attempt_at: string;
  /** Selected stores in this FSA — bounded, rendered inline; `[]` when none. */
  affected_stores: AdminCoverageFailureStore[];
}

export interface AdminCoverageFailureListResponse {
  failures: AdminCoverageFailure[];
  total: number;
}

/**
 * The FSAs whose coverage re-attempts are exhausted (the operator worklist),
 * each with its latest-fetch details + the selected stores it affects. Uniform
 * list params (sort/limit/offset); default sort last-attempt desc. `total` is
 * the real count of exhausted FSAs.
 */
export function fetchCoverageFailures(
  params?: AdminQueryParams,
): Promise<AdminCoverageFailureListResponse> {
  return getJson<AdminCoverageFailureListResponse>(
    `/api/admin/coverage-failures${listQuery(params)}`,
  );
}

/**
 * Manually re-trigger a coverage check for one FSA — enqueues a background Flipp
 * probe with the FSA's recorded postal (the same check the daily cron runs). The
 * check is ASYNC: coverage lands on a later cycle, not synchronously, so a 2xx
 * means "queued", not "fixed". 404 when the FSA isn't an exhausted failure; the
 * thrown error carries `.status` + the server `{ error }` message.
 */
export function recheckCoverage(fsa: string): Promise<{ queued: boolean }> {
  return mutateJson<{ queued: boolean }>(
    `/api/admin/coverage-failures/${encodeURIComponent(fsa)}/recheck`,
    'POST',
    {},
  );
}

// --- Integrations (T65.2.1.1) — ingestion monitoring + manual recovery ------

/** Mirrors `SYNC_RUN_STATUSES` (`backend/src/retailer-offers/sync-db-port.ts`),
 *  the one `syncruns.status` vocabulary — ARCHITECTURE_CONTRACT § SyncRun.
 *  There is no value meaning "partly": coverage is a counter, not a status. */
export type IntegrationRunStatus = 'running' | 'completed' | 'failed';

/** One run's telemetry — mirrors the backend's `IntegrationRunSummary`. */
export interface IntegrationRunSummary {
  id: string;
  status: IntegrationRunStatus;
  started_at: string;
  finished_at: string | null;
  /** The channel's "volume" for this run (its written/ingested-item counter). */
  volume: number;
  /** A REAL failure message only — `null` for a skip-telemetry-only run (T72;
   *  see `skipped_merchants`). Never carries the raw JSON blob. */
  error: string | null;
  /** T72 — flyers the consumer gate skipped this run (Flipp only; `[]` otherwise). */
  skipped_merchants: RunSkippedMerchant[];
  /** A `running` row older than the backend's presumed-dead threshold (~2h) —
   *  shown as dead, doesn't block a manual re-run. */
  presumed_dead: boolean;
}

/** Flipp's coverage line inputs ("N of M regions OK — K failing"). */
export interface IntegrationCoverageSummary {
  total: number;
  without_coverage: number;
}

export interface IntegrationChannelFlags {
  quiet: boolean;
  stale: boolean;
  failed: boolean;
}

export interface IntegrationChannelSummary {
  source: string;
  label: string;
  latest_run: IntegrationRunSummary | null;
  /** Median volume of the channel's last ~8 completed runs; `null` with no
   *  history yet. */
  norm: number | null;
  flags: IntegrationChannelFlags;
  /** Flipp only. */
  coverage?: IntegrationCoverageSummary;
}

export interface IntegrationWorkerStatus {
  alive: boolean;
  /** Present only when `alive` is false. */
  reason?: string;
  jobs_waiting: number;
}

export interface IntegrationsAggregateResponse {
  channels: IntegrationChannelSummary[];
  worker: IntegrationWorkerStatus;
}

/** The at-a-glance read: both live channels' latest run + volume norm + flags,
 *  Flipp's coverage tally, and the worker health / jobs-waiting signal (BUG49). */
export function fetchIntegrations(): Promise<IntegrationsAggregateResponse> {
  return getJson<IntegrationsAggregateResponse>('/api/admin/integrations');
}

/** One region's outcome on a Flipp run (`postalfetches`). */
export interface IntegrationRunRegion {
  fsa: string;
  postal_code: string;
  outcome: string;
  item_count: number;
  flyer_count: number;
  error: string | null;
  attempt: number;
}

export interface IntegrationRunDetail extends IntegrationRunSummary {
  duration_ms: number | null;
  rows_inserted: number;
  rows_updated: number;
  rows_delisted: number;
  /** Flipp only — this run's per-region outcomes. */
  regions?: IntegrationRunRegion[];
}

export interface IntegrationRunsResponse {
  runs: IntegrationRunDetail[];
  total: number;
}

/** Paged run history for one live channel (`source` is the channel's route id,
 *  e.g. `'flipp'` / `'kays-fresh-market-site'`). Rejects with "Unknown
 *  integration source" on 404 (a non-live source). */
export function fetchIntegrationRuns(
  source: string,
  params: { limit?: number; offset?: number } = {},
): Promise<IntegrationRunsResponse> {
  const sp = new URLSearchParams();
  if (params.limit != null) sp.set('limit', String(params.limit));
  if (params.offset != null) sp.set('offset', String(params.offset));
  const qs = sp.toString();
  return getJson<IntegrationRunsResponse>(
    `/api/admin/integrations/${encodeURIComponent(source)}/runs${qs ? `?${qs}` : ''}`,
  );
}

/**
 * Manual re-run (live channels only). The check is ASYNC: a 2xx means
 * "queued", not "finished" — the run shows up in the history once the worker
 * picks it up. Rejects with the server's `{ error }` message on 409 (already
 * running) or 400 (a non-live source); the thrown error carries `.status`.
 */
export function triggerIntegrationRun(source: string): Promise<{ queued: boolean }> {
  return mutateJson<{ queued: boolean }>(
    `/api/admin/integrations/${encodeURIComponent(source)}/run`,
    'POST',
    {},
  );
}

// --- Ingestion flows (T65.12 read + T65.13 Prepare; T65.14 admin UI) --
//
// Reads (`fetchFlows`/`fetchFlow`/`fetchIngestionRuns`/`fetchIngestionItems`/
// `fetchIngestionCalibration`) hit routes the backend already serves. The two
// PATCH writers (`updateFlow`/`updateFlowJobSettings`) call the paths T65.14's
// spec documents for a parallel backend lane (Deliverable 1) that had not
// landed the routes at the time this UI shipped — they 404 until that lane
// ships them; the Settings zones stage edits locally and only call these on
// Save, so the round trip lights up the moment the backend does, with no UI
// change needed. Run-now reuses the LIVE `triggerIntegrationRun` above (T65.2.1.1) —
// a flow's `sourcekey` (e.g. `flipp:weekly-flyers`) is `source:stream`; the part
// before the colon is the integration `source` param.

export interface FlowTrigger {
  key: string;
  kind: 'schedule' | 'event' | 'api';
  is_enabled: boolean;
  /** kind-specific fields — `cron` (schedule), `event`/`source` (event), `name` (api). */
  [extra: string]: unknown;
}

export interface FlowJob {
  key: string;
  settings: Record<string, unknown>;
  /** Code-sourced, never stored: Prepare action keys declared in code but not
   *  yet implemented (T65.19 D5) — e.g. `propose-product`. `undefined` for a
   *  job with none. */
  notbuilt?: string[];
}

export interface FlowRow {
  key: string;
  name: string;
  sourcekind: 'external' | 'internal';
  sourcekey: string;
  triggers: FlowTrigger[];
  jobs: FlowJob[];
  is_enabled: boolean;
  settings: Record<string, unknown>;
}

export function fetchFlows(): Promise<{ flows: FlowRow[] }> {
  return getJson<{ flows: FlowRow[] }>('/api/admin/flows');
}

export function fetchFlow(key: string): Promise<{ flow: FlowRow }> {
  return getJson<{ flow: FlowRow }>(`/api/admin/flows/${encodeURIComponent(key)}`);
}

/** T65.14 Deliverable 1 — pending backend route; see the section note above. */
export function updateFlow(
  key: string,
  patch: { is_enabled?: boolean; name?: string; triggers?: FlowTrigger[]; budget?: { weeklyTokenCap: number } },
): Promise<{ flow: FlowRow }> {
  return mutateJson<{ flow: FlowRow }>(`/api/admin/flows/${encodeURIComponent(key)}`, 'PATCH', patch);
}

/** T65.14 Deliverable 1 — pending backend route; see the section note above. */
export function updateFlowJobSettings(
  key: string,
  jobKey: string,
  settings: Record<string, unknown>,
): Promise<{ flow: FlowRow }> {
  return mutateJson<{ flow: FlowRow }>(
    `/api/admin/flows/${encodeURIComponent(key)}/jobs/${encodeURIComponent(jobKey)}/settings`,
    'PATCH',
    settings,
  );
}

// --- Arbitrate job (T65.16 Stage 5) -----------------------------------------
//
// Settings page = configuration only (batching/budget/dispatch/prompt via the
// real declaration migration 0023 seeded; grants read-only, publish shown
// OFF); management (pending/planned/in-flight/recorded, batch log, spend vs
// the run token cap, Run now/Pause/Resume/Re-form batches) renders with the
// run (owner ruling). Three-outcome vocabulary only (T65.25 ratified
// amendment): `approved | escalated | rejected`.
//
// T65.39.1 Step 6 (D4) completion — execution and the confidence threshold
// are runner-entry concerns now, configured on this screen's own "Runners"
// section (`/api/admin/review-runners`, `ReviewRunnerWriteInput` below), never
// here: this response type carries neither field any more.

export type ReviewGroupingKey = 'retailer' | 'none';

export interface ReviewBatchingSettings {
  groupingKeys: ReviewGroupingKey[];
  chunkSize: number;
  sharedContextPreprompt: boolean;
}

export interface ReviewDispatchSettings {
  maxParallelAgents: number;
  runTokenCap: number;
  queue: string;
  /** T65.32 review-stall-2026-10-17 — how long the runner lets one claimed
   *  session run before ending it, in minutes. */
  sessionTimeoutMinutes: number;
}

/** Mirrors `backend/src/ingestion/flows/jobs/review/reviewAdminService.ts`'s
 *  `ReviewJobConfigSettings` (decoded — the backend's own decoder, never
 *  re-derived client-side). */
export interface ReviewSettings {
  batching: ReviewBatchingSettings;
  tokenBudgetPerBatch: number;
  dispatch: ReviewDispatchSettings;
  promptVersion: string;
  preprompt: string;
}

export interface ReviewGrant {
  key: string;
  enabled: boolean;
}

/** T65.25 D2 / T65.29 — the declared review jobs. `arbitrate` keeps its key
 *  (renaming it would churn every stored declaration for zero behavior) and
 *  reads "Single review"; `combo-review` is the second; `escalated-review`
 *  (T65.29) is the third — one extra strongest-model pass before a confirmed
 *  escalation reaches the Owner's queue. */
export type ReviewJobKey = 'arbitrate' | 'combo-review' | 'escalated-review';

export interface ReviewJobRow {
  jobKey: ReviewJobKey;
  label: string;
}

/** T65.39.3 D1/D4 — flow-level, shown beside this job's own dispatch dials. */
export interface ReviewWeeklyBudget {
  cap: number | null;
  spent: number;
  credit: number;
  resetsAt: string;
}

export interface ReviewCliBackoffRow {
  cli: 'claude' | 'codex';
  backedOffUntil: string | null;
}

export interface ReviewJobSettingsResponse {
  jobKey: ReviewJobKey;
  label: string;
  settings: ReviewSettings;
  grants: ReviewGrant[];
  weeklyBudget: ReviewWeeklyBudget;
  cliBackoff: ReviewCliBackoffRow[];
  /** T65.39.3.2.1 AC4 — this job's own named formula parts and its raw-input
   *  description, read off the SAME declaration the Review batch detail's
   *  formula line and the composed AI review session input both derive from
   *  (`reviewInputDeclarationFromJobs`). Empty when the flow declares no
   *  composition for this job. */
  inputParts: string[];
  rawInputDescription: string;
  /** The job's own frozen input-shape version (e.g. `combo-input-v1`) — the
   *  SAME value a batch this job forms freezes onto its own `inputVersion`. */
  inputVersion: string;
}

/** Which review jobs this flow actually declares, in declaration order. */
export function fetchReviewJobs(flowKey: string): Promise<{ jobs: ReviewJobRow[] }> {
  return getJson<{ jobs: ReviewJobRow[] }>(`/api/admin/flows/${encodeURIComponent(flowKey)}/arbitrate/jobs`);
}

export function fetchReviewSettings(flowKey: string, jobKey: ReviewJobKey = 'arbitrate'): Promise<ReviewJobSettingsResponse> {
  return getJson<ReviewJobSettingsResponse>(`/api/admin/flows/${encodeURIComponent(flowKey)}/arbitrate/settings?job=${encodeURIComponent(jobKey)}`);
}

export function topUpReviewWeeklyBudget(flowKey: string, amount: number): Promise<ReviewJobSettingsResponse> {
  return mutateJson<ReviewJobSettingsResponse>(`/api/admin/flows/${encodeURIComponent(flowKey)}/arbitrate/weekly-budget/top-up`, 'POST', { amount });
}

export function resetReviewWeeklyBudget(flowKey: string): Promise<ReviewJobSettingsResponse> {
  return mutateJson<ReviewJobSettingsResponse>(`/api/admin/flows/${encodeURIComponent(flowKey)}/arbitrate/weekly-budget/reset`, 'POST', {});
}


/** Configuration-only patch — batching merges into the `form-batches` action;
 *  everything else merges into the job's own settings. Never includes
 *  `actions`/`key`/`state`/`grants` — the server rejects those (400).
 *  T65.39.1 Step 6 (D4) completion — `execution`/`decisionThreshold` dropped:
 *  the server rejects them too now (400, "Unknown or architecture field") —
 *  both are edited via `ReviewRunnerWriteInput` (`/api/admin/review-runners`)
 *  instead. */
export interface ReviewSettingsPatch {
  batching?: ReviewBatchingSettings;
  tokenBudget?: number;
  dispatch?: { maxParallelAgents: number; runTokenCap: number; queue: string; sessionTimeoutMinutes: number };
  /** Only `preprompt` is client-editable — the version bump is server-owned. */
  prompt?: { preprompt: string };
}

export function updateReviewSettings(
  flowKey: string,
  patch: ReviewSettingsPatch,
  jobKey: ReviewJobKey = 'arbitrate',
): Promise<ReviewJobSettingsResponse> {
  return mutateJson<ReviewJobSettingsResponse>(
    `/api/admin/flows/${encodeURIComponent(flowKey)}/arbitrate/settings?job=${encodeURIComponent(jobKey)}`,
    'PATCH',
    patch,
  );
}

export type ReviewOutcome = 'approved' | 'escalated' | 'rejected';

/** Mirrors `REVIEW_BATCH_STATUSES`
 *  (`backend/src/ingestion/flows/jobs/review/batch-statuses.ts`) —
 *  ARCHITECTURE_CONTRACT § IngestionArbitrateBatch. A status names where the
 *  batch is; why it is still there is `holdReason`. */
export type ReviewBatchStatus = 'waiting' | 'running' | 'completed' | 'failed';
export type ReviewBatchHoldReason = 'budget' | 'operator';

/** T65.39.7 AC2 — the five gates the claim path (`requestBatch`) itself
 *  distinguishes, mirrored 1:1 by the backend's `ReviewBatchStuckGate`
 *  (`executor.ts`). "weekly exhausted" and "run fused"/"budget>cap" are the
 *  same two claim-path predicates, so both read as one gate each. */
export type ReviewBatchStuckGate = 'operator-hold' | 'no-runner' | 'age-limit' | 'weekly-budget' | 'run-fuse';

export interface ReviewBatchStuckReason {
  gate: ReviewBatchStuckGate;
  numbers: Record<string, number | string | null>;
}

/** T65.39.3.3 Step 4/5 — mirrors the backend's `ReviewTokenSpend`
 *  (`reviewAdminService.ts`): the one token-spend shape a run, a batch and
 *  an item all render. `costUsd` is `null`, never `0`, when no CLI in scope
 *  reported a cost; `sessionCount === 0` is the scope's OWN "no sessions
 *  measured" signal — render that as "no sessions", never as zero spend. */
export interface ReviewTokenSpend {
  input: number;
  cacheCreation: number;
  cacheRead: number;
  output: number;
  total: number;
  costUsd: number | null;
  sessionCount: number;
  unaccountedSessions: number;
}

/** One batch-log row — strategy (group), items in, answered, tokens, outcome
 *  breakdown, prompt version (§ Placement's run-detail batch log). */
export interface ReviewBatchLogRow {
  id: string;
  status: ReviewBatchStatus;
  /** Why a `waiting` batch is not being offered, or `null` when nothing holds
   *  it: the run's token cap (`budget`) or an operator pause (`operator`). */
  holdReason: ReviewBatchHoldReason | null;
  /** Lease expiries spent, against `ReviewRunState.attemptCap`. */
  attempts: number;
  /** T65.25 D2 — the review job that formed this batch; batches never mix. */
  jobKey: ReviewJobKey;
  group: string;
  promptVersion: string;
  itemsIn: number;
  answered: number;
  outcomes: Record<ReviewOutcome, number>;
  tokens: ReviewTokenSpend;
  createdAt: string;
  updatedAt: string;
  /** T65.39.7 AC2 — which gate refuses this `waiting` batch right now, with
   *  its numbers; `null` for every other status, and for a `waiting` batch
   *  nothing currently refuses. */
  stuckReason: ReviewBatchStuckReason | null;
  /** T65.39.7 — the single-shot "Review now" mark, `null` when unset. */
  operatorDispatchAt: string | null;
  operatorDispatchExpired: boolean;
  operatorDispatchedBy: string | null;
}

export interface ReviewRunState {
  pending: number;
  waiting: number;
  running: number;
  completed: number;
  failed: number;
  attemptCap: number;
  batches: ReviewBatchLogRow[];
  spend: ReviewTokenSpend & { runTokenCap: number };
}

export function fetchReviewRunState(flowKey: string, runId: string): Promise<ReviewRunState> {
  return getJson<ReviewRunState>(
    `/api/admin/flows/${encodeURIComponent(flowKey)}/arbitrate/runs/${encodeURIComponent(runId)}`,
  );
}

/** One item inside an opened batch, in its `position` order. */
export interface ReviewBatchDetailItem {
  id: string;
  position: number;
  rawTitle: string;
  /** The ITEM's lifecycle status, not the batch's. */
  status: string;
  /** T65.39.3.2.2 fix round 4 (D4) — Prepare's own audit trail, for a closed
   *  fold only; the clean draft this item sent to the model is already in
   *  `ReviewBatchDetail.prompt`, so the raw draftConclusion isn't sent here. */
  prepareStages: unknown[];
  arbitrateVerdicts: unknown;
  arbitrateOutcome: ReviewOutcome | null;
  arbitrateEscalationReason: string | null;
}

/** T65.39 D13 — one batch opened. A pure read on the server: no render is
 *  triggered and no folder is written, so this works in EVERY status, and a
 *  `failed` batch is the one most worth opening. */
export interface ReviewBatchDetail {
  id: string;
  syncrunId: string;
  flowKey: string;
  jobKey: ReviewJobKey;
  label: string;
  status: ReviewBatchStatus;
  holdReason: ReviewBatchHoldReason | null;
  attempts: number;
  attemptCap: number;
  group: string;
  promptVersion: string;
  inputVersion: string;
  tokens: ReviewTokenSpend;
  createdAt: string;
  updatedAt: string;
  /** The ACTUAL model input, composed by the executor's own builder. */
  prompt: string;
  preprompt: string;
  /** T65.39.3.2.1 AC4 — the same formula parts and raw-input description the
   *  job's own configuration page's Input panel shows, read off this batch's
   *  own composed input, so the two never disagree. */
  formulaParts: string[];
  rawInputDescription: string;
  /** `null` when every item's raw input resolved; otherwise which item(s)
   *  did not and why. A refused batch still opens (200) — this is what the
   *  operator reads instead of a 500. */
  refusal: { reason: string; itemIds: string[] } | null;
  /** True when the batch exhausted its attempts and released its items back to
   *  the work set, so it legitimately has none left to show. */
  membersReleased: boolean;
  items: ReviewBatchDetailItem[];
  stuckReason: ReviewBatchStuckReason | null;
  operatorDispatchAt: string | null;
  operatorDispatchExpired: boolean;
  operatorDispatchedBy: string | null;
}

export function fetchReviewBatchDetail(flowKey: string, batchId: string): Promise<ReviewBatchDetail> {
  return getJson<ReviewBatchDetail>(
    `/api/admin/flows/${encodeURIComponent(flowKey)}/arbitrate/batches/${encodeURIComponent(batchId)}`,
  );
}

/** T65.39.7 (Deliverable 3) — "Review now" on ONE batch: the operator's own
 *  attributed spend authorization for exactly this batch, honored on the
 *  next runner poll ahead of the weekly/run-fuse budget refusals (never the
 *  age limit). Only a `waiting` batch on this flow accepts it (404 otherwise). */
export function reviewBatchNow(flowKey: string, batchId: string): Promise<{ batchId: string; operatorDispatchAt: string }> {
  return mutateJson<{ batchId: string; operatorDispatchAt: string }>(
    `/api/admin/flows/${encodeURIComponent(flowKey)}/arbitrate/batches/${encodeURIComponent(batchId)}/review-now`,
    'POST',
    {},
  );
}

/** Async — a 2xx means "queued" (or deduped by pg-boss's singleton, `queued:
 *  false`), never "finished". */
export function runReviewNow(flowKey: string, runId: string): Promise<{ queued: boolean }> {
  return mutateJson<{ queued: boolean }>(
    `/api/admin/flows/${encodeURIComponent(flowKey)}/arbitrate/runs/${encodeURIComponent(runId)}/run-now`,
    'POST',
    {},
  );
}

export function pauseReviewRun(flowKey: string, runId: string): Promise<{ paused: number }> {
  return mutateJson<{ paused: number }>(
    `/api/admin/flows/${encodeURIComponent(flowKey)}/arbitrate/runs/${encodeURIComponent(runId)}/pause`,
    'POST',
    {},
  );
}

export function resumeReviewRun(flowKey: string, runId: string): Promise<{ resumed: number }> {
  return mutateJson<{ resumed: number }>(
    `/api/admin/flows/${encodeURIComponent(flowKey)}/arbitrate/runs/${encodeURIComponent(runId)}/resume`,
    'POST',
    {},
  );
}

/** Only reshapes batches still `planned` — never an in-flight/recorded one. */
export function reformReviewBatches(flowKey: string, runId: string): Promise<{ batches: number }> {
  return mutateJson<{ batches: number }>(
    `/api/admin/flows/${encodeURIComponent(flowKey)}/arbitrate/runs/${encodeURIComponent(runId)}/reform-batches`,
    'POST',
    {},
  );
}

/** The cross-flow escalated queue — item, flow, written reason, age. */
export interface EscalatedReviewItem {
  id: string;
  rawTitle: string;
  flowKey: string | null;
  syncrunId: string;
  reason: string | null;
  decidedAt: string | null;
  ageMs: number | null;
}

export function fetchEscalatedReviewItems(limit = 100): Promise<{ items: EscalatedReviewItem[] }> {
  return getJson<{ items: EscalatedReviewItem[] }>(`/api/admin/arbitrate/escalated-items?limit=${limit}`);
}

/** T65.25 D4 — one answer per ruled product (mirrors
 *  `backend/src/ingestion/flows/jobs/review/mcpService.ts`'s `VerdictProduct`). Final
 *  values: the proposal and any fix are already merged, so this reads as a
 *  complete materialization instruction. */
export type ReviewProductAnswer =
  | { action: 'existing'; systemProductId: string; confidence: number }
  | { action: 'new'; name: string; brand: string | null; category: string | null; confidence: number }
  | { action: 'drop'; reason: string; confidence: number };

/** The verdict's own ruled offer values. `proposal` indexes the verdict's OWN
 *  product list, never the draft's. */
export interface ReviewVerdictOffer {
  basis: string | null;
  packing: Array<{ proposal: number; pack: number | null; size: number | null; unit: string | null }>;
}

/** The stored verdict body (`ingestionitems.arbitrateverdicts`). */
export interface ReviewVerdictBody {
  splitCorrected: boolean;
  products: ReviewProductAnswer[];
  offer: ReviewVerdictOffer;
}

/** T65.29 D5 — the item's ORIGINAL (escalated) verdict, archived once by the
 *  escalated-review job's replacement write before it overwrote the columns
 *  `IngestionItemReview` reads below. `null` for every item that has never
 *  been through that replacement (the common case). */
export interface IngestionItemFirstReviewVerdict {
  outcome: ReviewOutcome;
  verdicts: ReviewVerdictBody | null;
  escalationReason: string | null;
  model: string | null;
  promptVersion: string | null;
  cardVersion: string | null;
  decidedAt: string | null;
}

/** An item's review verdict, rendered in the item detail view (§ Placement).
 *  `null` when the line hasn't been reviewed yet (honest absence). */
export interface IngestionItemReview {
  outcome: ReviewOutcome;
  verdicts: ReviewVerdictBody | null;
  escalationReason: string | null;
  model: string | null;
  promptVersion: string | null;
  cardVersion: string | null;
  decidedAt: string | null;
  /** T65.29 D5 — the first (escalated) verdict this re-review replaced, for
   *  the side-by-side both-verdicts view. `null` unless this item went
   *  through an escalated-review replacement. */
  firstVerdict: IngestionItemFirstReviewVerdict | null;
}

/** Per-run accounting (T65.12): arrived = published + rejected + escalated
 *  + pending (items still awaiting review) + failed (approved items whose one
 *  publish attempt never landed). `pending`/`failed`/`reviewStalled` are
 *  optional — older single-run readers omit them; the list endpoint sends
 *  them so the equation balances while review is outstanding, and
 *  `reviewStalled` marks a run whose items waited past the backend's
 *  stale-undecided floor (review is stuck, not in progress). */
export interface IngestionRunAccounting {
  arrived: number;
  published: number;
  rejected: number;
  escalated: number;
  skippedUnchanged: number;
  /** T65.35.5 D4 — Flipp intake only; 0 on every other job's run. */
  duplicateSkip: number;
  unresolvedMerchantSkip: number;
  invalidItemSkip: number;
  pending?: number;
  failed?: number;
  reviewStalled?: boolean;
}

/** T72 follow-up — one flyer-skip telemetry entry (`syncruns.error`'s split-out
 *  shape; a REAL error stays on `error`, never mixed into this array). */
export interface RunSkippedMerchant {
  merchant: string | null;
  reason: 'unknown-merchant' | 'no-consumer';
  flyer_count: number;
}

/**
 * T65.14.2 D1 (rework round 2) — one follow-up run (coverage re-attempt /
 * rate-limit resume) this run spawned, rendered inside its detail drill —
 * never as a sibling list row (ARCHITECTURE_CONTRACT § SyncRun (Provenance)).
 */
export interface IngestionRunChild {
  id: string;
  status: string;
  /** Distinct FSAs this follow-up fetched. */
  fsas: string[];
  /** Highest recorded fetch attempt this follow-up wrote, `null` when none. */
  max_attempt: number | null;
}

export interface IngestionRunRow {
  id: string;
  adaptername: string;
  status: string;
  started_at: string;
  finished_at: string | null;
  /** A REAL failure message only — `null` for a skip-telemetry-only run (T72;
   *  see `skipped_merchants`). Never carries the raw JSON blob. */
  error: string | null;
  /** T72 — flyers the consumer gate skipped this run (Flipp only; `[]` otherwise). */
  skipped_merchants: RunSkippedMerchant[];
  flow_key: string;
  accounting: IngestionRunAccounting;
  /** T65.14.2 D1 (rework round 2) — the flow-declared trigger key that fired
   *  this run (resolve its display name from `flow.triggers` — never a
   *  client-side label map). `null` for a manually opened (Run now) or
   *  legacy pre-migration run. */
  triggerkey: string | null;
  /** T65.14.2 D1 — the acting admin's identity for a manually opened (Run
   *  now) run. `null` for a declared-trigger or legacy run. Never set
   *  alongside `triggerkey`. */
  actor: string | null;
  /** T65.14.2 D2 — distinct FSAs this run fetched (from `postalfetches`), empty
   *  when the run recorded none yet. */
  fsas: string[];
  /** T65.14.2 D2 — highest recorded fetch attempt this run wrote, `null` when none. */
  max_attempt: number | null;
  /** T65.14.2 D1/D2 — this run's follow-up runs (coverage re-attempts, resume
   *  continuations), for the run detail's follow-up section. Empty for a run
   *  with no follow-ups. Parent runs only ever appear at the list's top
   *  level; a follow-up never has its own top-level row. */
  children: IngestionRunChild[];
}

export function fetchIngestionRuns(limit = 50): Promise<{ runs: IngestionRunRow[] }> {
  return getJson<{ runs: IngestionRunRow[] }>(`/api/admin/ingestion/runs?limit=${limit}`);
}

/** Mirrors `backend/src/ingestion/flows/jobs/prepare/prepare.ts` `DraftFieldKey` —
 *  every scored field on a v3 draft (identity + packing). */
export type DraftFieldKey =
  | 'name'
  | 'brand'
  | 'pack'
  | 'size'
  | 'unit';

/** The one field-value shape: a real value with its source and score, or
 *  explicitly empty (`value` and `source` both null). */
export interface DraftField {
  value: unknown;
  source: 'parsed' | 'supplied' | 'inherited' | 'derived' | null;
  score: number | null;
}

export interface DraftStage {
  stage: 'screen' | 'parse' | 'lookup' | 'identity' | 'category';
  status: 'computed' | 'empty' | 'error';
  consumed: string;
  produced: Record<string, unknown> | null;
  scores: Record<string, number | null> | null;
  artifact: { name: string; version: string };
  error: string | null;
}

/** T65.25 D1 — the item's type. The retired `class` vocabulary conflated this
 *  with per-proposal match state; both now live where they belong. */
export type DraftType = 'single' | 'combo';

/** One Proposed SystemProduct — identity only (no size: sizes are packing
 *  observations on the offer, never part of a product's identity). */
export interface ProposedSystemProduct {
  name: DraftField;
  brand: DraftField;
  match: { existing: Array<{ id: string; score: number }> };
}

/** An observation about THIS line's packing; `proposal` indexes `proposed`. */
export interface ProposedOfferPacking {
  proposal: number;
  pack: DraftField;
  size: DraftField;
  unit: DraftField;
}

/** One offer per line — a combo's single offer covers any of its proposals. */
export interface ProposedOffer {
  price: { sale: string | null; regular: string | null; basis: string | null };
  conditions: string[];
  validity: { from: string | null; to: string | null };
  packing: ProposedOfferPacking[];
}

/** Mirrors `backend/src/ingestion/flows/jobs/prepare/prepare.ts` `DraftConclusion`. */
export interface DraftConclusion {
  version: 3;
  type: DraftType;
  proposed: ProposedSystemProduct[];
  offer: ProposedOffer;
  stages: DraftStage[];
}

/** The "no stored draft conclusion" facet sentinel (T65.14.1 delta ruling 1) —
 *  mirrors `backend/src/ingestion/reporting/ingestionReadService.ts`'s
 *  `NO_DRAFT_CONCLUSION_FILTER_VALUE`. Kept as one literal so the wire value
 *  the class filter sends and the honest-absence copy the table/modal render
 *  are the same words. */
export const NO_DRAFT_CONCLUSION_FILTER_VALUE = 'no extended observation recorded';

/** A published line's resolved deal link (T65.14.1 Deliverable 1's Result column). */
export interface IngestionItemPublishedDeal {
  id: string;
  title: string;
}

/** A SystemProduct a published line's deal links to. */
export interface IngestionItemPublishedSystemProduct {
  id: string;
  name: string;
}

export interface IngestionItemRow {
  id: string;
  syncrun_id: string;
  ingestionpayload_id: string;
  sourceitemid: string;
  rawtitle: string;
  pricefields: Record<string, unknown>;
  status: string;
  holdreason: string | null;
  currentjob: string | null;
  receivedat: string;
  decidedat: string | null;
  draftconclusion: DraftConclusion | null;
  /** Thumbnail, recovered from the archived payload — null when the source
   *  supplied no image (T65.14.1 Deliverable 1). */
  image_url: string | null;
  /** Reject reason (rejected lines only — the compact decision row's
   *  `result_reason`, a junk class). */
  reject_reason?: string | null;
  /** The flyer deal a published line resolved to, or `null` if it can't be
   *  found (honest absence, never fabricated). Published lines only. */
  published_deal?: IngestionItemPublishedDeal | null;
  /** The SystemProduct(s) the published deal links to — `[]` when none. */
  published_systemproducts?: IngestionItemPublishedSystemProduct[];
}

export interface IngestionItemsResponse {
  lines: IngestionItemRow[];
  total: number;
}

/**
 * `run` is required server-side (400 without it). T65.14.1 — server-paged +
 * filtered (the uniform list contract, T28.5): pass a `useAdminTable`'s
 * `params` for real pagination at run volume (AC4) — column filters land as
 * `filter.status` / `filter.class` / `filter.title`. Omitting `params` reads
 * the server's default page (today's back-compat shape for any caller that
 * still wants "the whole run" behavior loses out — see the server default).
 */
export function fetchIngestionItems(
  runId: string,
  params?: AdminQueryParams,
): Promise<IngestionItemsResponse> {
  const sp = params ? buildAdminListQuery(params) : new URLSearchParams();
  sp.set('run', runId);
  return getJson<IngestionItemsResponse>(`/api/admin/ingestion/lines?${sp.toString()}`);
}

/** The "As received" layer — the raw item exactly as the source delivered it,
 *  recovered from the archived payload (never a live re-fetch). `null` when
 *  no matching item can be found in the payload (honest absence). */
export interface IngestionItemReceived {
  title: string | null;
  /** T65.19 D4 — the RESOLVED Retailer's name (never a document field; the
   *  document carries no merchant field). `null` for an unmatched merchant,
   *  same honest-absence shape as the run-payloads read's `retailername`. */
  retailer_name: string | null;
  regular_price: string | null;
  sale_price: string | null;
  sale_description: string | null;
  price_basis: string | null;
  valid_from: string | null;
  valid_to: string | null;
  source_item_id: string | null;
  source_url: string | null;
  image_url: string | null;
  description: string | null;
  confidence: number | null;
}

/** The "Pipeline record" layer's payload metadata, plus (owner follow-up) the
 *  default-rendered envelope + this item's own slice — never the whole
 *  `deals` array (that's the separate on-demand `fetchIngestionPayloadBody`). */
export interface IngestionItemPayloadMeta {
  id: string;
  fetchedat: string;
  contentfingerprint: string;
  /** How many items rode in on this same archived payload. */
  sharedbylines: number;
  /** The stored payload's total size in bytes — shown before the on-demand
   *  full-body fetch loads. */
  byte_size: number;
  /** The Integration data envelope (schemaVersion/source/retailerId/sections),
   *  each section's `deals` array stripped — `null` when the payload can't be
   *  parsed (honest absence). */
  envelope: Record<string, unknown> | null;
  /** This item's own raw `DealItem` slice from the payload — `null` when no
   *  matching item survives (honest absence, mirrors `received`). */
  item_data: Record<string, unknown> | null;
}

/** The "Decision" layer — populated ONLY for a terminal outcome
 *  (published/rejected). `null` for received/escalated: the caller renders
 *  the honest undecided state directly from the line's own status/currentjob
 *  (reviewer ruling), never an invented decision. */
export interface IngestionItemDecision {
  status: 'published' | 'rejected';
  reason: string | null;
  currentjob: string | null;
  decidedat: string;
  published: {
    flyerdeal: IngestionItemPublishedDeal | null;
    systemproducts: IngestionItemPublishedSystemProduct[];
  } | null;
}

/** T65.39.3.3 Deliverable 3 (AC4) — an item's own token figure: its even
 *  share of every review batch it was a member of, summed across all of
 *  them. `derived: true` is part of the type, not only a comment — the
 *  members of one batch did not each cost the same. */
export interface IngestionItemReviewSpend extends ReviewTokenSpend {
  derived: true;
}

/** The one read powering the item modal (T65.14.1 Deliverable 3). */
export interface IngestionItemDetail {
  line: IngestionItemRow;
  received: IngestionItemReceived | null;
  payload: IngestionItemPayloadMeta | null;
  decision: IngestionItemDecision | null;
  /** T65.16 Stage 5 — Arbitrate's own per-item outcome, distinct from
   *  `decision` (the item's terminal published/rejected record); `null`
   *  before the line reaches Arbitrate. */
  arbitrate: IngestionItemReview | null;
  reviewSpend: IngestionItemReviewSpend;
}

/** 404s on an unknown line id. */
export function fetchIngestionItemDetail(id: string): Promise<IngestionItemDetail> {
  return getJson<IngestionItemDetail>(`/api/admin/ingestion/lines/${encodeURIComponent(id)}`);
}

/**
 * Owner follow-up — the explicit, on-demand "view raw payload" read: the FULL
 * archived Integration data body (every item; can be megabytes on a national run).
 * Never called as part of the modal's initial load — only from the Pipeline
 * record layer's explicit raw-payload toggle. 404s on an unknown payload id.
 */
export interface IngestionPayloadBody {
  id: string;
  fetchedat: string;
  contentfingerprint: string;
  /** The ORIGINAL stored bytes — unaffected by pretty-printing/truncation. */
  byte_size: number;
  /** Pretty-printed server-side (owner feedback: minified JSON was
   *  unreadable) and, when that pretty text is huge, truncated server-side to
   *  its first ~200KB — never re-parsed or truncated again in the browser. */
  body: string;
  /** Whether `body` is a truncated prefix of the full pretty text. */
  truncated: boolean;
}

export function fetchIngestionPayloadBody(id: string): Promise<IngestionPayloadBody> {
  return getJson<IngestionPayloadBody>(`/api/admin/ingestion/payloads/${encodeURIComponent(id)}`);
}

/**
 * Owner walkthrough follow-up — the run detail's Payloads section: one row per
 * archived payload of this run (never a per-line fan-out; the read is one
 * grouped query server-side). `sharedbylines` is how many `ingestionitems`
 * rows reference this payload.
 */
export interface IngestionRunPayloadRow {
  id: string;
  fetchedat: string;
  contentfingerprint: string;
  byte_size: number;
  sharedbylines: number;
  /** The resolved retailer name — `null` for an unmatched merchant (the
   *  caller renders the honest "Unmatched merchants" copy; display
   *  formatting, not a status vocabulary, stays client-side). */
  retailername: string | null;
  /** The integration data's postal-code-prefix eligibility list (from its
   *  envelope), server-resolved — never a client-side re-parse. */
  postalCodePrefixes: string[];
}

export function fetchIngestionRunPayloads(runId: string): Promise<{ payloads: IngestionRunPayloadRow[] }> {
  return getJson<{ payloads: IngestionRunPayloadRow[] }>(`/api/admin/ingestion/runs/${encodeURIComponent(runId)}/payloads`);
}

export interface IngestionCalibration {
  run: string | null;
  // Owner ruling (T65.14.1 round 11) — "no silent absence": these carry the
  // COMPLETE draftconclusion vocabulary so the component never hardcodes
  // which fields/classes exist. `fields` stays sparse (one row per (field,
  // decile) with at least one score); `classes` is ALWAYS zero-filled for
  // every `classKeys` entry, even ones with no lines yet.
  fieldKeys: string[];
  fields: Array<{ field: string; decile: number; count: number }>;
  /** T65.25 D1 — the retired single `class` facet, split into the two facts it
   *  conflated: the item's type, and each proposal's own match state. */
  typeKeys: string[];
  types: Array<{ type: string; count: number }>;
  matchStateKeys: string[];
  matchStates: Array<{ state: string; count: number }>;
  stages: Array<{ stage: string; status: string; count: number }>;
}

/** Omit `runId` for the cumulative (all-runs) read. */
export function fetchIngestionCalibration(runId?: string): Promise<IngestionCalibration> {
  const qs = runId ? `?run=${encodeURIComponent(runId)}` : '';
  return getJson<IngestionCalibration>(`/api/admin/ingestion/calibration${qs}`);
}

// --- Legal documents (T27.2.10.2.1) -----------------------------------------

/**
 * One row of the admin Legal status list: the document's published state +
 * version + date + publisher, and `draft_differs` — true when the bundled draft
 * has drifted from the published copy (or nothing is published yet), i.e. a
 * Publish would change the live page.
 */
export interface AdminLegalDocument {
  slug: string;
  title: string;
  published: boolean;
  published_version: number | null;
  published_at: string | null;
  published_by: string | null;
  draft_differs: boolean;
}

export interface AdminLegalListResponse {
  documents: AdminLegalDocument[];
}

/** The published-vs-bundled status of every legal document. */
export function fetchLegalDocuments(): Promise<AdminLegalListResponse> {
  return getJson<AdminLegalListResponse>('/api/admin/legal');
}

/** The published row returned by a Publish. */
export interface PublishedLegalDocument {
  slug: string;
  content: string;
  version: number;
  published_at: string | null;
  published_by: string | null;
}

/**
 * Publish one document — snapshot its bundled draft into the published row, bump
 * the version, stamp the admin as publisher. The public legal page updates with no
 * app release. 404 on an unknown slug.
 */
export function publishLegalDocument(slug: string): Promise<PublishedLegalDocument> {
  return mutateJson<PublishedLegalDocument>(
    `/api/admin/legal/${encodeURIComponent(slug)}/publish`,
    'POST',
    {},
  );
}

// --- Feature ideas board (T35.7.1) ------------------------------------------
//
// The PM curates/publishes/holds/orders ideas; users upvote published ones.
// Behind the same `/api/admin` gate. Shapes mirror `adminFeatureIdeasService`.

/** Published / Held / Archived — derived state (no build lifecycle). */
export type FeatureIdeaState = 'published' | 'held' | 'archived';

/** The list filter chips. */
export type FeatureIdeaFilter = 'all' | 'newsug' | 'published' | 'held' | 'archived';

/** The list order. */
export type FeatureIdeaSort = 'manual' | 'votes';

/** A status transition the PM can apply. */
export type FeatureIdeaTransition = 'publish' | 'hold' | 'archive' | 'unarchive';

/** One idea row in the admin list (hero fields + live signals). */
export interface AdminFeatureIdeaRow {
  id: string;
  title: string;
  description: string | null;
  state: FeatureIdeaState;
  position: number;
  source: string;
  is_newsuggestion: boolean;
  upvotes: number;
  /** Comment count = the vote rows carrying a free-text "why". */
  comments: number;
  /** Dollars backed on this idea (active backings) — the row's 💜 Paid dim. */
  total_paid: number;
  /** Days live since published_at (to unpublished_at or now); 0 when never published. */
  days_live: number;
  published_at: string | null;
  unpublished_at: string | null;
}

/** The live per-chip counts for the list header. */
export interface AdminFeatureIdeaCounts {
  all: number;
  newsug: number;
  published: number;
  held: number;
  archived: number;
}

export interface AdminFeatureIdeaListResponse {
  ideas: AdminFeatureIdeaRow[];
  counts: AdminFeatureIdeaCounts;
}

/** The detail's statistics tiles. */
export interface AdminFeatureIdeaStatistics {
  upvotes: number;
  comments: number;
  from: number;
  days_live: number;
}

/** One per-user engagement row (vote + their "why" + when + their backing). */
export interface AdminFeatureIdeaEngagementRow {
  user_email: string | null;
  household_name: string | null;
  direction: string | null;
  why: string | null;
  when: string | null;
  /** Dollars this user backed this idea (active backing), or null when none. */
  paid: number | null;
  /** The recurring label ('Weekly'/'Biweekly'/'Monthly'), or null (one-time / none). */
  recurring: string | null;
}

/** The per-idea backing roll-up (the "Total paid · N backers · recurring" summary). */
export interface AdminFeatureIdeaBackingSummary {
  total_paid: number;
  backers: number;
  recurring_total: number;
}

/** One history entry (append-only, display-only). */
export interface AdminFeatureIdeaHistoryEntry {
  action: string;
  by: string;
  at: string;
}

export interface AdminFeatureIdeaDetail {
  id: string;
  title: string;
  description: string | null;
  internalnotes: string | null;
  state: FeatureIdeaState;
  position: number;
  source: string;
  is_newsuggestion: boolean;
  published_at: string | null;
  unpublished_at: string | null;
  statistics: AdminFeatureIdeaStatistics;
  backing: AdminFeatureIdeaBackingSummary;
  engagement: AdminFeatureIdeaEngagementRow[];
  history: AdminFeatureIdeaHistoryEntry[];
}

/** The create / edit payload. */
export interface FeatureIdeaInput {
  title: string;
  description?: string | null;
  internal_notes?: string | null;
  publish?: boolean;
}

/** The filtered + sorted list with the live per-chip counts. */
export function fetchFeatureIdeas(
  filter: FeatureIdeaFilter = 'all',
  sort: FeatureIdeaSort = 'manual',
): Promise<AdminFeatureIdeaListResponse> {
  const sp = new URLSearchParams();
  sp.set('filter', filter);
  sp.set('sort', sort);
  return getJson<AdminFeatureIdeaListResponse>(`/api/admin/feature-ideas?${sp.toString()}`);
}

/** One idea, full detail (hero + facts + statistics + engagement + history). 404 unknown. */
export function fetchFeatureIdea(id: string): Promise<AdminFeatureIdeaDetail> {
  return getJson<AdminFeatureIdeaDetail>(`/api/admin/feature-ideas/${encodeURIComponent(id)}`);
}

/** Create a manual idea (held by default; `publish:true` publishes now). 400 bad input. */
export function createFeatureIdea(input: FeatureIdeaInput): Promise<AdminFeatureIdeaDetail> {
  return mutateJson<AdminFeatureIdeaDetail>('/api/admin/feature-ideas', 'POST', input);
}

/** Edit an idea (any subset of title/description/internal_notes/publish). 404 unknown. */
export function updateFeatureIdea(
  id: string,
  input: Partial<FeatureIdeaInput>,
): Promise<AdminFeatureIdeaDetail> {
  return mutateJson<AdminFeatureIdeaDetail>(
    `/api/admin/feature-ideas/${encodeURIComponent(id)}`,
    'PATCH',
    input,
  );
}

/** Apply a status transition (each logs history; archive forces published off). */
export function transitionFeatureIdea(
  id: string,
  action: FeatureIdeaTransition,
): Promise<AdminFeatureIdeaDetail> {
  return mutateJson<AdminFeatureIdeaDetail>(
    `/api/admin/feature-ideas/${encodeURIComponent(id)}/transition`,
    'POST',
    { action },
  );
}

/** Renumber the manual order. `orderedIds` is the full desired order. */
export function reorderFeatureIdeas(orderedIds: string[]): Promise<{ reordered: true }> {
  return mutateJson<{ reordered: true }>('/api/admin/feature-ideas/reorder', 'POST', {
    orderedIds,
  });
}

/** Triage a new suggestion (clear the flag, log "Reviewed"). 404 unknown. */
export function triageFeatureIdea(id: string): Promise<AdminFeatureIdeaDetail> {
  return mutateJson<AdminFeatureIdeaDetail>(
    `/api/admin/feature-ideas/${encodeURIComponent(id)}/triage`,
    'POST',
    {},
  );
}

/** Hard-delete an idea (CASCADEs links + votes). 404 unknown. */
export function deleteFeatureIdea(id: string): Promise<DeleteResult> {
  return deleteJson<DeleteResult>(`/api/admin/feature-ideas/${encodeURIComponent(id)}`);
}

// --- Quick questions (T35.5) ------------------------------------------------

/**
 * One targeting criterion. `type` is open-ended (richer criteria are an additive
 * layer); V1 understands `location` (region substring) + `has_product` (product
 * substring). Extra keys carry the criterion's value (`region` / `product`).
 */
export interface TargetingCriterion {
  type: string;
  [key: string]: unknown;
}

/** The targeting spec: everyone, or a filtered set of ANDed criteria. */
export type TargetingSpec =
  | { mode: 'everyone' }
  | { mode: 'filtered'; criteria: TargetingCriterion[] };

export interface AdminQuickQuestion {
  id: string;
  text: string;
  answer_type: string;
  choices: string[] | null;
  targeting: TargetingSpec;
  active: boolean;
  created_at: string;
  created_by: string | null;
  response_count: number;
}

export interface AdminQuickQuestionsResponse {
  questions: AdminQuickQuestion[];
}

export interface QuickQuestionInput {
  text: string;
  answer_type: string;
  choices?: string[] | null;
  targeting?: TargetingSpec;
  active?: boolean;
}

/** One answer bucket for a choice/reaction question. */
export interface QuestionResponseBucket {
  value: string;
  count: number;
}

/** One free-text answer (a sentence question, captured as an R2 feedback item). */
export interface QuestionFreeTextAnswer {
  id: string;
  text: string;
  created_at: string;
}

export interface QuestionResponsesResponse {
  question: AdminQuickQuestion;
  answered: number;
  buckets: QuestionResponseBucket[];
  free_text: QuestionFreeTextAnswer[];
  free_text_total: number;
}

/** List all authored quick questions (active + paused), newest-first. */
export function fetchQuickQuestions(): Promise<AdminQuickQuestionsResponse> {
  return getJson<AdminQuickQuestionsResponse>('/api/admin/quick-questions');
}

/** Read one quick question (decoded). 404 unknown. */
export function fetchQuickQuestion(id: string): Promise<AdminQuickQuestion> {
  return getJson<AdminQuickQuestion>(`/api/admin/quick-questions/${encodeURIComponent(id)}`);
}

/** Author a new quick question. 400 on a missing text / bad answer type. */
export function createQuickQuestion(input: QuickQuestionInput): Promise<AdminQuickQuestion> {
  return mutateJson<AdminQuickQuestion>('/api/admin/quick-questions', 'POST', input);
}

/** Edit a quick question (full replace of the editable fields). 404 unknown. */
export function updateQuickQuestion(
  id: string,
  input: QuickQuestionInput,
): Promise<AdminQuickQuestion> {
  return mutateJson<AdminQuickQuestion>(
    `/api/admin/quick-questions/${encodeURIComponent(id)}`,
    'PATCH',
    input,
  );
}

/** Toggle whether the question is shown (`active`). 404 unknown. */
export function setQuickQuestionActive(
  id: string,
  active: boolean,
): Promise<AdminQuickQuestion> {
  return mutateJson<AdminQuickQuestion>(
    `/api/admin/quick-questions/${encodeURIComponent(id)}/active`,
    'POST',
    { active },
  );
}

/** Count the households a targeting spec selects (the "reaches N households" preview). */
export function previewTargeting(
  targeting: TargetingSpec,
): Promise<{ household_count: number; targeting: TargetingSpec }> {
  return mutateJson<{ household_count: number; targeting: TargetingSpec }>(
    '/api/admin/quick-questions/preview-targeting',
    'POST',
    { targeting },
  );
}

/** Answers grouped + counted for one question. 404 unknown. */
export function fetchQuestionResponses(id: string): Promise<QuestionResponsesResponse> {
  return getJson<QuestionResponsesResponse>(
    `/api/admin/quick-questions/${encodeURIComponent(id)}/responses`,
  );
}

// --- Report a Problem inbox (T35.6.4) ----------------------------------------
//
// The admin side of the standalone `reportproblems` table (ADR-012 / T35.6).
// Reads/writes go through `adminReportProblemsService`.

/** One row in the admin report-problems list. */
export interface AdminReportProblemListItem {
  id: string;
  /** null when the user was deleted (SET NULL FK); household still exists. */
  reporter_email: string | null;
  household_name: string | null;
  note: string | null;
  severity: 'minor' | 'major' | 'blocked' | null;
  screen_context: string | null;
  status: 'new' | 'progress' | 'resolved' | 'archived';
  created_at: string;
  is_consent_to_contact: boolean;
  screenshot_count: number;
}

export interface AdminReportProblemListResponse {
  reports: AdminReportProblemListItem[];
  total: number;
  /** Count of open (new + progress) items — the inbox badge value. */
  open_count: number;
}

/** One history entry in the status audit trail. */
export interface AdminReportHistoryEntry {
  act: string;
  by: string;
  at: string;
}

/** Full detail for one report. */
export interface AdminReportProblemDetail {
  id: string;
  reporter_email: string | null;
  household_name: string | null;
  note: string | null;
  severity: 'minor' | 'major' | 'blocked' | null;
  screen_context: string | null;
  item_context: Record<string, unknown> | null;
  env: Record<string, unknown> | null;
  screenshots: unknown[];
  status: 'new' | 'progress' | 'resolved' | 'archived';
  admin_notes: string | null;
  status_history: AdminReportHistoryEntry[];
  is_consent_to_contact: boolean;
  created_at: string;
  updated_at: string;
}

export interface AdminReportProblemListParams {
  search?: string;
  status?: string[];
  severity?: string[];
  sortKey?: string;
  sortDir?: 'asc' | 'desc';
}

/** List all report-problems — no source filter. Supports search / filters / sort. */
export function fetchReportProblems(
  params: AdminReportProblemListParams = {},
): Promise<AdminReportProblemListResponse> {
  const q = new URLSearchParams();
  if (params.search) q.set('search', params.search);
  if (params.status && params.status.length > 0) {
    params.status.forEach((s) => q.append('filter.status', s));
  }
  if (params.severity && params.severity.length > 0) {
    params.severity.forEach((s) => q.append('filter.severity', s));
  }
  if (params.sortKey) {
    q.set('sort', params.sortDir ? `${params.sortKey}:${params.sortDir}` : params.sortKey);
  }
  const qs = q.toString();
  return getJson<AdminReportProblemListResponse>(`/api/admin/report-problems${qs ? `?${qs}` : ''}`);
}

/** Full detail for one report. 404 throws if not found. */
export function fetchReportProblem(id: string): Promise<AdminReportProblemDetail> {
  return getJson<AdminReportProblemDetail>(`/api/admin/report-problems/${encodeURIComponent(id)}`);
}

/** Move a report to a new status; appends a history row. Returns updated detail. */
export function setReportProblemStatus(
  id: string,
  status: string,
): Promise<AdminReportProblemDetail> {
  return mutateJson<AdminReportProblemDetail>(
    `/api/admin/report-problems/${encodeURIComponent(id)}/status`,
    'PATCH',
    { status },
  );
}

/** Persist admin-only internal notes (never shown to reporter). Returns updated detail. */
export function saveReportProblemNotes(
  id: string,
  notes: string | null,
): Promise<AdminReportProblemDetail> {
  return mutateJson<AdminReportProblemDetail>(
    `/api/admin/report-problems/${encodeURIComponent(id)}/notes`,
    'PATCH',
    { notes },
  );
}

// --- Subscriptions + Cancellations console (T35.7.2.5) -----------------------
//
// Read-only admin surfaces over the `subscriptions` + `cancellations` tables.
// The server derives every figure once (Account/Paid-Free, monthly-equiv, plan
// summaries, glance + reason-mix); the UI renders finished strings/numbers.
// Every amount is the pledge PER INTERVAL — never dollars collected (no ledger).

/** One row in the all-subscriptions list (the active book). */
export interface AdminSubscriptionListItem {
  id: string;
  user_id: string | null;
  /** Subscriber email; null only if the user cascade-deleted (defensive). */
  user_email: string | null;
  household_name: string | null;
  type: string;
  type_label: string;
  idea_title: string | null;
  recurring: string;
  billing: 'Recurring' | 'One-time';
  billing_label: string;
  amount_cents: number;
  amount_display: string;
  currency: string;
  status: string;
  status_label: string;
  started_at: string;
}

/** The four glance-bar figures (Paid users derive from `users`, not subs). */
export interface AdminSubscriptionGlance {
  paid_users: number;
  free_users: number;
  active_plans: number;
  /** Recurring monthly-equivalent across all active recurring plans, in cents. */
  recurring_monthly_cents: number;
}

export interface AdminSubscriptionListResponse {
  subscriptions: AdminSubscriptionListItem[];
  total: number;
  glance: AdminSubscriptionGlance;
}

export interface AdminSubscriptionListParams {
  search?: string;
  status?: string[];
  type?: string[];
  /** Billing facet — values 'Recurring' / 'One-time'. */
  billing?: string[];
  sortKey?: string;
  sortDir?: 'asc' | 'desc';
}

/** List every subscription across users + the glance bar. Read-only. */
export function fetchSubscriptions(
  params: AdminSubscriptionListParams = {},
): Promise<AdminSubscriptionListResponse> {
  const q = new URLSearchParams();
  if (params.search) q.set('search', params.search);
  if (params.status?.length) params.status.forEach((s) => q.append('filter.status', s));
  if (params.type?.length) params.type.forEach((t) => q.append('filter.type', t));
  if (params.billing?.length) params.billing.forEach((b) => q.append('filter.billing', b));
  if (params.sortKey) {
    q.set('sort', params.sortDir ? `${params.sortKey}:${params.sortDir}` : params.sortKey);
  }
  const qs = q.toString();
  return getJson<AdminSubscriptionListResponse>(`/api/admin/subscriptions${qs ? `?${qs}` : ''}`);
}

/** One row in the cancellations list (the churn book). */
export interface AdminCancellationListItem {
  id: string;
  reason: string;
  reason_label: string;
  note: string | null;
  user_email: string | null;
  household_name: string | null;
  user_id: string | null;
  subscription_id: string;
  subscription_type: string;
  subscription_type_label: string;
  idea_title: string | null;
  subscription_recurring: string;
  subscription_billing_label: string;
  subscription_amount_cents: number;
  subscription_amount_display: string;
  created_at: string;
}

/** One reason-mix bucket (only reasons actually seen). */
export interface AdminCancellationReasonCount {
  reason: string;
  reason_label: string;
  count: number;
}

export interface AdminCancellationListResponse {
  cancellations: AdminCancellationListItem[];
  total: number;
  reason_mix: AdminCancellationReasonCount[];
}

export interface AdminCancellationListParams {
  search?: string;
  reason?: string[];
  sortKey?: string;
  sortDir?: 'asc' | 'desc';
}

/** List every cancellation joined sub→user + the reason mix. Read-only. */
export function fetchCancellations(
  params: AdminCancellationListParams = {},
): Promise<AdminCancellationListResponse> {
  const q = new URLSearchParams();
  if (params.search) q.set('search', params.search);
  if (params.reason?.length) params.reason.forEach((r) => q.append('filter.reason', r));
  if (params.sortKey) {
    q.set('sort', params.sortDir ? `${params.sortKey}:${params.sortDir}` : params.sortKey);
  }
  const qs = q.toString();
  return getJson<AdminCancellationListResponse>(`/api/admin/cancellations${qs ? `?${qs}` : ''}`);
}

// ---------------------------------------------------------------------------
// T55.4 — System products console: Categories / Attributes / Needs attention / Stats
// ---------------------------------------------------------------------------

export interface AdminCategoryTreeSynonym {
  id: string;
  synonym: string;
}

export interface AdminCategoryTreeNode {
  id: string;
  name: string;
  parent_category_id: string | null;
  status: 'active' | 'retired';
  origin: string;
  product_count: number;
  has_products: boolean;
  synonyms: AdminCategoryTreeSynonym[];
}

export interface AdminCategoryTreeResponse {
  categories: AdminCategoryTreeNode[];
}

export function fetchAdminCategoryTree(): Promise<AdminCategoryTreeResponse> {
  return getJson<AdminCategoryTreeResponse>('/api/admin/categories/tree');
}

export function createAdminCategory(name: string, parentCategoryId?: string | null): Promise<{ id: string }> {
  return mutateJson('/api/admin/categories', 'POST', { name, parent_category_id: parentCategoryId ?? null });
}

export function renameAdminCategory(id: string, name: string): Promise<{ id: string }> {
  return mutateJson(`/api/admin/categories/${encodeURIComponent(id)}`, 'PATCH', { name });
}

export function mergeAdminCategories(survivorId: string, loserId: string): Promise<{ survivor_id: string }> {
  return mutateJson(`/api/admin/categories/${encodeURIComponent(survivorId)}/merge`, 'POST', { loser_id: loserId });
}

export function reparentAdminCategory(id: string, parentCategoryId: string | null): Promise<{ id: string }> {
  return mutateJson(`/api/admin/categories/${encodeURIComponent(id)}/reparent`, 'POST', {
    parent_category_id: parentCategoryId,
  });
}

export function retireAdminCategory(id: string): Promise<{ id: string }> {
  return mutateJson(`/api/admin/categories/${encodeURIComponent(id)}/retire`, 'POST', {});
}

export function restoreAdminCategory(id: string): Promise<{ id: string }> {
  return mutateJson(`/api/admin/categories/${encodeURIComponent(id)}/restore`, 'POST', {});
}

export function addAdminCategorySynonym(id: string, synonym: string): Promise<{ id: string }> {
  return mutateJson(`/api/admin/categories/${encodeURIComponent(id)}/synonyms`, 'POST', { synonym });
}

export function removeAdminCategorySynonym(id: string, synonymId: string): Promise<{ removed: boolean }> {
  return deleteJson(`/api/admin/categories/${encodeURIComponent(id)}/synonyms/${encodeURIComponent(synonymId)}`);
}

export interface AdminAttributeSynonym {
  id: string;
  synonym: string;
}

export interface AdminAttributeListItem {
  id: string;
  name: string;
  status: 'new' | 'active' | 'retired';
  origin: string;
  product_count: number;
  has_products: boolean;
  synonyms: AdminAttributeSynonym[];
  categories: Array<{ id: string; name: string }>;
}

export interface AdminAttributeListResponse {
  attributes: AdminAttributeListItem[];
}

export function fetchAdminAttributeList(): Promise<AdminAttributeListResponse> {
  return getJson<AdminAttributeListResponse>('/api/admin/systemproductattributes');
}

export function setAdminAttributeCategories(id: string, categoryIds: string[]): Promise<{ id: string }> {
  return mutateJson(`/api/admin/systemproductattributes/${encodeURIComponent(id)}/categories`, 'PUT', { category_ids: categoryIds });
}

export interface AdminAttributeQueueItem {
  id: string;
  name: string;
  product_link_count: number;
  sample_products: string[];
}

export interface AdminAttributeQueueReject {
  id: string;
  name: string;
  reason: string;
  created_at: string;
}

export interface AdminAttributeQueueResponse {
  items: AdminAttributeQueueItem[];
  active_attributes: Array<{ id: string; name: string }>;
  mechanical_rejects: AdminAttributeQueueReject[];
}

export function fetchAdminAttributeQueue(): Promise<AdminAttributeQueueResponse> {
  return getJson<AdminAttributeQueueResponse>('/api/admin/attribute-queue');
}

export function createAdminAttribute(name: string): Promise<{ id: string }> {
  return mutateJson('/api/admin/systemproductattributes', 'POST', { name });
}

export function renameAdminAttribute(id: string, name: string): Promise<{ id: string }> {
  return mutateJson(`/api/admin/systemproductattributes/${encodeURIComponent(id)}`, 'PATCH', { name });
}

export function mergeAdminAttributes(survivorId: string, loserId: string): Promise<{ survivor_id: string }> {
  return mutateJson(`/api/admin/systemproductattributes/${encodeURIComponent(survivorId)}/merge`, 'POST', {
    loser_id: loserId,
  });
}

export function retireAdminAttribute(id: string): Promise<{ id: string }> {
  return mutateJson(`/api/admin/systemproductattributes/${encodeURIComponent(id)}/retire`, 'POST', {});
}

export function restoreAdminAttribute(id: string): Promise<{ id: string }> {
  return mutateJson(`/api/admin/systemproductattributes/${encodeURIComponent(id)}/restore`, 'POST', {});
}

export function addAdminAttributeSynonym(id: string, synonym: string): Promise<{ id: string }> {
  return mutateJson(`/api/admin/systemproductattributes/${encodeURIComponent(id)}/synonyms`, 'POST', { synonym });
}

export function removeAdminAttributeSynonym(id: string, synonymId: string): Promise<{ removed: boolean }> {
  return deleteJson(
    `/api/admin/systemproductattributes/${encodeURIComponent(id)}/synonyms/${encodeURIComponent(synonymId)}`,
  );
}

export function approveAdminAttributeCandidate(id: string): Promise<{ id: string }> {
  return mutateJson(`/api/admin/systemproductattributes/${encodeURIComponent(id)}/approve`, 'POST', {});
}

export function renameAndApproveAdminAttributeCandidate(id: string, name: string): Promise<{ id: string }> {
  return mutateJson(`/api/admin/systemproductattributes/${encodeURIComponent(id)}/rename-and-approve`, 'POST', {
    name,
  });
}

export function mergeAdminAttributeCandidate(id: string, survivorId: string): Promise<{ survivor_id: string }> {
  return mutateJson(`/api/admin/systemproductattributes/${encodeURIComponent(id)}/merge-candidate`, 'POST', {
    survivor_id: survivorId,
  });
}

export function reclassifyAdminAttributeCandidate(
  id: string,
  categoryName: string,
  parentCategoryId?: string | null,
): Promise<{ category_id: string; repointed_links: number }> {
  return mutateJson(`/api/admin/systemproductattributes/${encodeURIComponent(id)}/reclassify`, 'POST', {
    category_name: categoryName,
    parent_category_id: parentCategoryId ?? null,
  });
}

/** Never a hard delete server-side — flips `status: new → retired` (T55.4 Boundaries contract 2). */
export function deleteAdminAttributeCandidate(id: string): Promise<{ id: string }> {
  return mutateJson(`/api/admin/systemproductattributes/${encodeURIComponent(id)}/delete-candidate`, 'POST', {});
}

/** Per-tag suppression-aware removal (Boundaries contract 1) — distinct from the bulk create/edit picker. */
export function removeProductCategoryTag(
  systemProductId: string,
  categoryId: string,
): Promise<AdminSystemProductDetail> {
  return deleteJson(
    `/api/admin/systemproducts/${encodeURIComponent(systemProductId)}/categories/${encodeURIComponent(categoryId)}`,
  );
}

/**
 * Per-tag ADD (T65.1.3 D1) — the durable counterpart to `removeProductCategoryTag`:
 * a `source: 'manual', weight: 1.0` row. Rejects with `.status === 409` (message
 * names the existing link's state — manual / tombstoned / derived) when a link
 * row already exists for this pair; `.status === 400` on an unknown category.
 */
export function addProductCategoryTag(
  systemProductId: string,
  categoryId: string,
): Promise<AdminSystemProductDetail> {
  return mutateJson<AdminSystemProductDetail>(
    `/api/admin/systemproducts/${encodeURIComponent(systemProductId)}/categories/${encodeURIComponent(categoryId)}`,
    'POST',
    {},
  );
}

/** Per-product Attribute add (T65.1.4 G6); the server accepts active attributes only. */
export function addProductAttributeTag(
  systemProductId: string,
  attributeId: string,
): Promise<AdminSystemProductDetail> {
  return mutateJson(
    `/api/admin/systemproducts/${encodeURIComponent(systemProductId)}/attributes/${encodeURIComponent(attributeId)}`,
    'POST',
    {},
  );
}

export function removeProductAttributeTag(
  systemProductId: string,
  attributeId: string,
): Promise<AdminSystemProductDetail> {
  return deleteJson(
    `/api/admin/systemproducts/${encodeURIComponent(systemProductId)}/attributes/${encodeURIComponent(attributeId)}`,
  );
}

export interface AdminInboxPipeItem {
  systemproduct_id: string;
  name: string;
  live_deal_count: number;
  reason: 'no coverage' | 'unsure match';
}

export interface AdminInboxGapNote {
  searchevent_id: string;
  note: string;
}

export interface AdminInboxGapItem {
  normalized_query: string;
  search_count: number;
  zero_result: boolean;
  confirmed_miss: boolean;
  notes: AdminInboxGapNote[];
  state: 'open' | 'reopened';
}

export interface AdminInboxAttributeItem {
  id: string;
  name: string;
  product_link_count: number;
}

export interface AdminInboxResponse {
  pipe: AdminInboxPipeItem[];
  gap: AdminInboxGapItem[];
  attribute: AdminInboxAttributeItem[];
  counts: { pipe: number; gap: number; attribute: number; total: number };
}

export function fetchAdminInbox(): Promise<AdminInboxResponse> {
  return getJson<AdminInboxResponse>('/api/admin/inbox');
}

// ---------------------------------------------------------------------------
// T65.1.3 — Placement queue (absorbs the pipe lane above from System products)
// ---------------------------------------------------------------------------

export interface AdminPlacementCategoryRef {
  id: string;
  name: string;
}

export interface AdminPlacementItem {
  systemproduct_id: string;
  name: string;
  live_deal_count: number;
  reason: 'no coverage' | 'unsure match';
  /** The row's live (non-tombstoned) Category tags — its current placements. */
  current_placements: AdminPlacementCategoryRef[];
}

export interface AdminPlacementLaneCounts {
  no_coverage: number;
  unsure_match: number;
  total: number;
}

export interface AdminPlacementLaneResponse {
  items: AdminPlacementItem[];
  counts: AdminPlacementLaneCounts;
  total: number;
  limit: number;
  offset: number;
}

export function fetchPlacementLane(limit = 50, offset = 0): Promise<AdminPlacementLaneResponse> {
  return getJson<AdminPlacementLaneResponse>(`/api/admin/placement?limit=${limit}&offset=${offset}`);
}

/** The Placement queue's "no issue" ruling — durable dismissal, never resurfaces. 404 unknown. */
export function keepPlacement(id: string, reason?: string): Promise<{ id: string }> {
  return mutateJson<{ id: string }>(
    `/api/admin/systemproducts/${encodeURIComponent(id)}/placement-keep`,
    'POST',
    { reason },
  );
}

export interface TopSearchRow {
  normalized_query: string;
  count: number;
  outcome: 'results' | 'nothing' | 'nothing picked';
}

export interface HowTheySearchShares {
  multi_keyword_share: number;
  chips_share: number;
  typed_share: number;
  exclude_used_share: number;
  typo_corrected_share: number;
  synonym_translated_share: number;
}

export interface WhatAnsweredShares {
  name_share: number;
  category_share: number;
  attribute_share: number;
  brand_share: number;
}

export interface StatsPeriodBlock {
  top_searches: TopSearchRow[];
  how_they_search: HowTheySearchShares;
  what_answered: WhatAnsweredShares;
}

export interface AdminSearchStatsResponse {
  period_days: 7 | 30;
  current: StatsPeriodBlock;
  previous: StatsPeriodBlock;
  machine_context: {
    last_sync_at: string | null;
    tagged_count: number;
    flagged_count: number;
  };
}

export function fetchAdminSearchStats(period: 7 | 30): Promise<AdminSearchStatsResponse> {
  return getJson<AdminSearchStatsResponse>(`/api/admin/search-stats?period=${period}`);
}

export interface AdminQrRule {
  position: number;
  conditionKind: string | null;
  conditionValue: string | null;
  destinationUrl: string;
}

export interface AdminQrCode {
  id: string;
  slug: string;
  label: string;
  notes: string | null;
  isActive: boolean;
  svg: string | null;
  createdAt: string;
  updatedAt: string;
  rules: AdminQrRule[];
  scanCount: number;
  lastScannedAt: string | null;
  /** Server-derived from `QR_PUBLIC_BASE_URL` + slug — render verbatim, never
   *  assemble client-side (DS §0). */
  liveUrl: string;
  /** Non-crawler scans in the last 7 days (DS §6). */
  scansLast7d: number;
}

export interface AdminQrCodeInput {
  slug: string;
  label: string;
  notes?: string | null;
  isActive?: boolean;
  rules: Array<Pick<AdminQrRule, 'conditionKind' | 'conditionValue' | 'destinationUrl'>>;
}

export interface AdminQrCodesResponse {
  codes: AdminQrCode[];
  /** Server-derived from the same `QR_PUBLIC_BASE_URL` value the generator uses. */
  publicBaseUrl: string;
}

export function fetchQrCodes(): Promise<AdminQrCodesResponse> {
  return getJson<AdminQrCodesResponse>('/api/admin/qr-codes');
}

export function createQrCode(input: AdminQrCodeInput): Promise<{ code: AdminQrCode }> {
  return mutateJson('/api/admin/qr-codes', 'POST', input);
}

export function updateQrCode(slug: string, input: AdminQrCodeInput): Promise<{ code: AdminQrCode }> {
  return mutateJson(`/api/admin/qr-codes/${encodeURIComponent(slug)}`, 'PATCH', input);
}

export function deactivateQrCode(slug: string): Promise<{ code: AdminQrCode }> {
  return mutateJson(`/api/admin/qr-codes/${encodeURIComponent(slug)}/deactivate`, 'POST', {});
}

export function reactivateQrCode(slug: string): Promise<{ code: AdminQrCode }> {
  return mutateJson(`/api/admin/qr-codes/${encodeURIComponent(slug)}/reactivate`, 'POST', {});
}

/**
 * Hard-delete a never-scanned code (T74 follow-up). Resolves on 200 (`{ deleted:
 * true }`); rejects with the server's message on 409 (the code has at least one
 * scan — deactivate instead) or "QR code not found" on 404.
 */
export function deleteQrCode(slug: string): Promise<DeleteResult> {
  return deleteJson<DeleteResult>(`/api/admin/qr-codes/${encodeURIComponent(slug)}`);
}

export function generateQrCode(slug: string): Promise<{ code: AdminQrCode }> {
  return mutateJson(`/api/admin/qr-codes/${encodeURIComponent(slug)}/generate`, 'POST', {});
}

export async function downloadQrCodeSvg(slug: string): Promise<string> {
  try { return await http().getText(`/api/admin/qr-codes/${encodeURIComponent(slug)}.svg`); }
  catch (error) { return legacyError(error, `SVG download failed: ${(error as ApiError).status}`); }
}

// --- Review runners ---------------------------------------------------------
export interface AdminReviewRunner {
  key: string; placement: string; placementTab: string | null; placementPane: string | null; autorestart: boolean;
  scope: string | null; workspace: string | null; resolvedStartCommand: string | null;
  startAttempts: number; lastStartAt: string | null; lastStartOutcome: string | null; lastStartReason: string | null;
  cli: 'claude' | 'codex'; model: string; effort: string; maxTurns: number; prompt: string | null; job: string; slots: number; enabled: boolean; confidenceThreshold: number; createdAt: string; updatedAt: string;
  context: { flowMaxParallelAgents: number; runningCount: number } | null;
}
export interface ReviewRunnerWriteInput {
  key: string; placement: string; placementTab: string | null; placementPane: string | null; autorestart: boolean;
  cli: 'claude' | 'codex'; model: string; effort: string; maxTurns: number; prompt: string | null; job: string; slots: number; enabled: boolean; confidenceThreshold: number;
}
export interface AdminReviewRunnerCatalog { models: Record<'claude' | 'codex', string[]>; efforts: Record<'claude' | 'codex', string[]>; }
export function fetchReviewRunners(job?: string): Promise<{ runners: AdminReviewRunner[]; context?: { flowMaxParallelAgents: number; runningCount: number; runnerStartMode: 'manual' | 'awc' } | null; catalog?: AdminReviewRunnerCatalog }> { const query = job ? `?job=${encodeURIComponent(job)}` : ''; return getJson(`/api/admin/review-runners${query}`); }
export function createReviewRunner(input: ReviewRunnerWriteInput): Promise<AdminReviewRunner> { return mutateJson('/api/admin/review-runners', 'POST', input); }
export function updateReviewRunner(key: string, patch: Partial<ReviewRunnerWriteInput>): Promise<AdminReviewRunner> { return mutateJson(`/api/admin/review-runners/${encodeURIComponent(key)}`, 'PATCH', patch); }
export function deleteReviewRunner(key: string): Promise<DeleteResult> { return deleteJson<DeleteResult>(`/api/admin/review-runners/${encodeURIComponent(key)}`); }
// T65.39.4.1 D9 — `started` and `submitted` (awc145.4's rename) are both success.
export function startReviewRunner(key: string): Promise<{ outcome: 'started' | 'submitted' | 'refused' | 'failed'; reason: string }> { return mutateJson(`/api/admin/review-runners/${encodeURIComponent(key)}/start`, 'POST', {}); }
